import { TravelpayoutsWidget } from "@/components/travelpayouts-widget";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { SearchForm } from "@/components/search-form";
import { Header, Footer } from "@/components/layout";
import { FlightCard, type LogoSize } from "@/components/flight-card";
import { searchFlights } from "@/lib/flights.functions";
import { SEARCH_TIMEOUT_MS } from "@/lib/search-timing";
import { allLegsHaveCarryOn, allLegsHaveCheckedIncluded } from "@/lib/flight-meta";
import { findAirport, expandMetro } from "@/lib/airports";
import { decideResultsView } from "@/lib/results-view-state";
import { airlineName } from "@/lib/airline-names";
import { cityFromGen, cityToGen } from "@/lib/seo-routes";
import { formatSearchDatePl } from "@/lib/format-search-date";
import { parseDateList, serializeDateList, buildDatePairs, isContiguousRange, validateDateListRaw } from "@/lib/multi-date";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Loader2, AlertTriangle, ArrowDown, ArrowUp, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FlightResultsSkeleton } from "@/components/loading-skeletons";
import {
  saveFlightResult,
  loadFlightResult,
  markFanoutStart,
  wasRecentlyStarted,
} from "@/lib/search-progress-cache";
import { trackSearchCancel, trackSearchRestart } from "@/lib/analytics";
import { toast } from "sonner";


import { buildSearchFaq } from "@/lib/search-faq";
import { toApiParams } from "@/lib/search-api-params";
import { classifySearchError } from "@/lib/classify-search-error";

import { formatPairsStatus, formatPairsStatusAria } from "@/lib/format-pairs-status";
import { QueueSparkline } from "@/components/queue-sparkline";
import { HelpCircle, WifiOff, Briefcase, Luggage } from "lucide-react";


// `dd` i `rd` mogą zawierać pojedynczą datę, listę po przecinku albo skrót
// `from..to`. Walidacja składniowa robiona jest w `validateDateListRaw` —
// tutaj akceptujemy dowolny string (z twardym limitem długości), żeby
// błędny URL nie crashował całej trasy, a komponent pokazał czytelny
// komunikat w UI i pozwolił poprawić zapytanie.
const DATE_RAW_MAX = 800;
const searchSchema = z.object({
  ot: z.enum(["OneWay", "RoundTrip"]).default("RoundTrip"),
  from: z.string().length(3),
  to: z.string().length(3),
  dd: z.string().min(1).max(DATE_RAW_MAX),
  rd: z.string().max(DATE_RAW_MAX).optional(),
  a: z.number().int().min(1).max(9).default(1),
  c: z.number().int().min(0).max(9).default(0),
  i: z.number().int().min(0).max(9).default(0),
  sc: z.enum(["economy", "economy-premium", "business", "first"]).default("economy"),
  d: z.union([z.literal(0), z.literal(1)]).default(0),
  page: z.number().int().min(1).max(100).default(1),
  // Filtry odzwierciedlone w URL, by mogły wpływać na title/description (nadal noindex).
  stops: z.enum(["any", "direct", "1stop"]).default("any"),
  airline: z.string().max(8).default("all"),
  via: z.string().max(8).default("all"),
  bag: z.enum(["any", "carry", "checked"]).default("any"),
  sort: z.enum(["recommended", "price-asc", "price-desc", "duration", "soonest"]).default("price-asc"),
  // Parametry wyszukiwarki Travelpayouts — muszą zostać w URL.
  origin: z.string().max(8).optional(),
  destination: z.string().max(8).optional(),
  depart_date: z.string().max(10).optional(),
  return_date: z.string().max(10).optional(),
  adults: z.coerce.string().max(2).optional(),
  flightSearch: z.coerce.string().max(200).optional(),
  ticketId: z.coerce.string().max(300).optional(),
  marker: z.coerce.string().max(100).optional(),
  origin_airports: z.coerce.string().max(200).optional(),
  destination_airports: z.coerce.string().max(200).optional(),
});

// Wartości domyślne — usuwane z URL przez stripSearchParams, żeby linki były
// krótkie (bez śmieci typu &page=1&stops=any&bag=any).
const SEARCH_DEFAULTS: Partial<z.infer<typeof searchSchema>> = {
  ot: "RoundTrip",
  a: 1,
  c: 0,
  i: 0,
  sc: "economy",
  d: 0,
  page: 1,
  stops: "any",
  airline: "all",
  via: "all",
  bag: "any",
  sort: "price-asc",
};

const STOPS_LABEL: Record<"any" | "direct" | "1stop", string> = {
  any: "",
  direct: "loty bezpośrednie",
  "1stop": "max 1 przesiadka",
};
export const Route = createFileRoute("/search")({
  // Wyszukiwarka Travelpayouts sama przepisuje URL (np. ?flightSearch=...),
  // więc brak naszych parametrów nie może wywalać strony.
  validateSearch: (s) => {
    const r = searchSchema.safeParse(s);
    if (r.success) return r.data;
    const raw = s as Record<string, unknown>;
    return searchSchema.parse({
      from: "WAW", to: "BCN", dd: "2026-01-01",
      ...Object.fromEntries(
        ["flightSearch", "ticketId", "marker", "origin_airports", "destination_airports"]
          .filter((k) => raw[k] != null)
          .map((k) => [k, String(raw[k])]),
      ),
    });
  },
  search: { middlewares: [stripSearchParams(SEARCH_DEFAULTS) as never] },
  loaderDeps: ({ search }) => ({
    from: search.from,
    to: search.to,
    dd: search.dd,
    rd: search.rd,
    page: search.page,
    stops: search.stops,
    airline: search.airline,
  }),
  loader: ({ deps }) => deps,
  head: ({ loaderData }) => {
    const search = loaderData;
    const fromA = search?.from ? findAirport(search.from.toUpperCase()) : null;
    const toA = search?.to ? findAirport(search.to.toUpperCase()) : null;
    const page = search?.page ?? 1;
    const stops = search?.stops ?? "any";
    const airline = (search?.airline ?? "all").toUpperCase();
    const pageSuffix = page > 1 ? ` — strona ${page}` : "";

    const filterParts: string[] = [];
    if (stops !== "any") filterParts.push(STOPS_LABEL[stops]);
    if (airline !== "ALL") filterParts.push(`linia ${airline}`);
    const filterTitle = filterParts.length ? ` · ${filterParts.join(", ")}` : "";
    const filterDesc = filterParts.length ? ` Filtry: ${filterParts.join(", ")}.` : "";

    const SITE = "https://tanielatanie.pl";
    // Canonical zawiera ZNORMALIZOWANE dd/rd (parseDateList → serializeDateList),
    // żeby różne reprezentacje tego samego zbioru dat zlewały się do jednego
    // URL-a w indeksie:
    //   ?dd=2026-06-03,2026-06-01,2026-06-02   ─┐
    //   ?dd=2026-06-01..2026-06-03              ├─→ canonical: dd=2026-06-01..2026-06-03
    //   ?dd=2026-06-01,2026-06-02,2026-06-03   ─┘
    // Filtry (stops/airline/bag/sort) i parametry pasażerów NIE wchodzą do
    // canonical — wszystkie warianty z filtrami wskazują na podstawową
    // stronę wyników danej trasy+dat.
    const buildCanonical = (fromCode: string, toCode: string, p: number) => {
      const params = new URLSearchParams({ from: fromCode, to: toCode });
      const ddNorm = serializeDateList(parseDateList(search?.dd));
      if (ddNorm) params.set("dd", ddNorm);
      const rdNorm = serializeDateList(parseDateList(search?.rd));
      if (rdNorm) params.set("rd", rdNorm);
      if (p > 1) params.set("page", String(p));
      return `${SITE}/search?${params.toString()}`;
    };

    if (fromA && toA) {
      const fromGen = cityFromGen(fromA.city);
      const toGen = cityToGen(toA.city);
      const title = `Loty ${fromA.city} → ${toA.city}${filterTitle}${pageSuffix} | Tanie Latanie`;
      const baseDesc =
        page > 1
          ? `Strona ${page} wyników: tanie bilety lotnicze z ${fromGen} do ${toGen} (${fromA.code} → ${toA.code}). Porównaj kolejne oferty linii lotniczych.`
          : `Najtańsze bilety lotnicze z ${fromGen} do ${toGen} (${fromA.code} → ${toA.code}). Porównaj ceny linii lotniczych w jednym miejscu.`;
      const desc = baseDesc + filterDesc;
      const canonical = buildCanonical(fromA.code, toA.code, page);
      const faq = buildSearchFaq({
        fromCity: fromA.city, toCity: toA.city,
        fromCode: fromA.code, toCode: toA.code,
      });
      return {
        meta: [
          { title },
          { name: "description", content: desc },
          // Strony >1 i wyniki ze szczegółowymi parametrami nie są kanoniczne dla SEO.
          { name: "robots", content: "noindex,follow" },
          { property: "og:title", content: title },
          { property: "og:description", content: desc },
          { property: "og:url", content: canonical },
          { property: "og:type", content: "website" },
          { property: "og:site_name", content: "Tanie Latanie" },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: title },
          { name: "twitter:description", content: desc },
        ],
        links: [
          { rel: "canonical", href: canonical },
        ],
        scripts: [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
        ],
      };
    }
    const fallbackTitle = `Wyniki wyszukiwania${pageSuffix} — Tanie Latanie`;
    const fallbackDesc = page > 1
      ? `Strona ${page} wyników wyszukiwania lotów. Porównaj najtańsze bilety lotnicze.`
      : "Najtańsze loty porównane w jednym miejscu.";
    return {
      meta: [
        { title: fallbackTitle },
        { name: "description", content: fallbackDesc },
        { name: "robots", content: "noindex,follow" },
        { property: "og:title", content: fallbackTitle },
        { property: "og:description", content: fallbackDesc },
        { property: "og:url", content: page > 1 ? `${SITE}/search?page=${page}` : `${SITE}/search` },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Tanie Latanie" },
      ],
    };
  },
  component: TpSearchPage,
});

type Sort = "recommended" | "price-asc" | "price-desc" | "duration" | "soonest";
type StopsFilter = "any" | "direct" | "1stop";

// Pierwsza strona = 25 ofert (zamiast 10), kolejne porcje po 50.
// Skyscanner/Kayak też pokazują dużo wyników od razu — paginacja po 10 to
// frustracja przy dużym fan-oucie. Auto-load przez IntersectionObserver
// uruchamia kolejną porcję, gdy user doscrolluje do końca listy.
const INITIAL_PAGE_SIZE = 25;
const PAGE_STEP = 50;
function visibleCountForPage(p: number): number {
  return INITIAL_PAGE_SIZE + Math.max(0, p - 1) * PAGE_STEP;
}

// Etykieta dat dla nagłówka wyników:
//   - 1 data           → "15 maja"
//   - ciągły zakres    → "15 maja – 22 maja"
//   - niesąsiadujące   → "15 maja + (3 dni)"
function formatDateListPl(dates: string[]): string {
  if (dates.length === 0) return "";
  if (dates.length === 1) return formatSearchDatePl(dates[0]);
  if (isContiguousRange(dates)) {
    return `${formatSearchDatePl(dates[0])} – ${formatSearchDatePl(dates[dates.length - 1])}`;
  }
  return `${formatSearchDatePl(dates[0])} + (${dates.length} dni)`;
}

function TpSearchPage() {
  return (
    <main className="container mx-auto px-4 py-8">
      <h1 className="font-display text-2xl font-bold sm:text-3xl">Wyszukaj tanie loty</h1>
      <div className="mt-5"><TravelpayoutsWidget /></div>
    </main>
  );
}

// Poprzednia wyszukiwarka (eSky) — nieużywana.
export function SearchPage() {
  const s = Route.useSearch();
  const navigate = Route.useNavigate();

  // Mobile: wyłączamy pull-to-refresh na stronie wyników. Każde przypadkowe
  // pociągnięcie palcem w dół na ekranie ładowania robiło hard-reload, który
  // ucinał in-flight fan-out i zmuszał użytkownika do startu od zera (sygnał
  // widoczny w adminie jako event `/_event/search-restart`). `overscroll-behavior-y:
  // contain` na <html> blokuje natywne pull-to-refresh, nie ruszając zwykłego
  // scrolla. Przywracamy poprzednią wartość przy unmount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const prev = root.style.overscrollBehaviorY;
    root.style.overscrollBehaviorY = "contain";
    return () => {
      root.style.overscrollBehaviorY = prev;
    };
  }, []);


  // Loguje wyszukiwanie do `search_queries`, gdy user trafia na /search z
  // gotowymi parametrami (np. z /destinations albo z udostępnionego linku) —
  // bez submitu formularza nic by tu nie wpadło i Live ścieżki w adminie
  // pokazywałyby tylko URL, bez kierunku. Deduplikacja per sesja+kierunek+daty
  // w sessionStorage, żeby zmiany filtrów (stops/bag/sort) nie generowały
  // nowych wpisów.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!s.from || !s.to || !s.dd) return;
    // Dedup z TTL 5 min: powtórne wejście w tę samą trasę po dłuższej
    // przerwie powinno odświeżyć wpis w `search_queries`, żeby Live ścieżki
    // w adminie widziały aktualne wyszukiwanie (admin odcina wpisy starsze
    // niż 3 h). Zmiany filtrów klienckich (sort/bag/stops) nie zmieniają
    // klucza, więc dalej nie generują duplikatów.
    const key = `sq:${s.from}:${s.to}:${s.dd}:${s.rd ?? ""}:${s.ot}:${s.a}:${s.c}:${s.i}:${s.sc}:${s.d}`;
    const TTL_MS = 5 * 60_000;
    try {
      const prev = Number(sessionStorage.getItem(key) ?? 0);
      if (prev && Date.now() - prev < TTL_MS) return;
      sessionStorage.setItem(key, String(Date.now()));
    } catch {
      // brak sessionStorage — wtedy może być duplikat, trudno
    }
    import("@/lib/analytics").then(({ getSessionId }) => {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/search_queries`;
      const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      // bierzemy pierwszą datę z listy (multi-date), żeby wpis był spójny
      // z tym, co pokazujemy w Live ścieżki (jedna data lotu)
      const firstDd = s.dd.split(",")[0] || null;
      const firstRd = s.rd ? s.rd.split(",")[0] || null : null;
      void fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          apikey,
          Authorization: `Bearer ${apikey}`,
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          session_id: getSessionId(),
          from_iata: s.from,
          to_iata: s.to,
          depart_date: firstDd,
          return_date: s.ot === "RoundTrip" ? firstRd : null,
          pax: s.a + s.c + s.i,
          trip_type: s.ot,
          service_class: s.sc,
          direct_only: s.d === 1,
        }),
      }).catch(() => {});
    }).catch(() => {});
  }, [s.from, s.to, s.dd, s.rd, s.ot, s.a, s.c, s.i, s.sc, s.d]);


  const sort = s.sort;
  const setSort = (v: Sort) =>
    navigate({ search: (prev: typeof s) => ({ ...prev, sort: v, page: 1 }) });
  // Rozmiar logo linii lotniczych — dobierany automatycznie do szerokości ekranu.
  const [logoSize, setLogoSize] = useState<LogoSize>("md");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const pick = (): LogoSize => {
      const w = window.innerWidth;
      if (w < 640) return "sm";
      if (w < 1024) return "md";
      return "lg";
    };
    const apply = () => setLogoSize(pick());
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);
  const stopsFilter = s.stops;
  const airlineFilter = s.airline;
  const viaFilter = s.via;
  const bagFilter = s.bag;
  const setStopsFilter = (v: StopsFilter) =>
    navigate({ search: (prev: typeof s) => ({ ...prev, stops: v, page: 1 }) });
  const setAirlineFilter = (v: string) =>
    navigate({ search: (prev: typeof s) => ({ ...prev, airline: v, page: 1 }) });
  const setViaFilter = (v: string) =>
    navigate({ search: (prev: typeof s) => ({ ...prev, via: v, page: 1 }) });
  const setBagFilter = (v: typeof bagFilter) =>
    navigate({ search: (prev: typeof s) => ({ ...prev, bag: v, page: 1 }) });
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const page = s.page;
  const setPage = (p: number) => navigate({ search: (prev: typeof s) => ({ ...prev, page: p }), resetScroll: false });

  // UWAGA: `params` celowo NIE zawiera `page`, `stops`, ani `airline` — to
  // stan UI (paginacja + filtry klienta), nie wejście backendu. Dorzucenie ich
  // tu spowoduje, że queryKey się zmieni przy „Pokaż więcej" i React Query
  // wystartuje niepotrzebny refetch (loader + reset wyników). Patrz test
  // `search-api-params.test.ts`.
  const baseParams = useMemo(() => toApiParams(s), [s]);

  // Multi-date: rozpakowujemy `dd` i `rd` na listy i budujemy iloczyn
  // (depart × return). Dla pojedynczych dat pairs ma długość 1 — zachowanie
  // identyczne jak przed wprowadzeniem multi-date.
  const departList = useMemo(() => parseDateList(s.dd), [s.dd]);
  const returnList = useMemo(() => parseDateList(s.rd), [s.rd]);
  // Walidacja UX: jeżeli raw z URL jest niepuste, ale parser nic nie wyciągnął
  // (zły separator, odwrócony zakres, śmieci) — pokażemy alert nad listą.
  const ddError = useMemo(() => validateDateListRaw(s.dd), [s.dd]);
  const rdError = useMemo(
    () => (s.ot === "RoundTrip" ? validateDateListRaw(s.rd) : null),
    [s.ot, s.rd],
  );
  const dateInputErrors = useMemo(
    () => [
      ddError ? { field: "Data wylotu", err: ddError } : null,
      rdError ? { field: "Data powrotu", err: rdError } : null,
    ].filter(Boolean) as Array<{ field: string; err: { code: string; message: string } }>,
    [ddError, rdError],
  );
  const { pairs: datePairs, truncated: dateTruncated, total: dateTotal } = useMemo(
    () => buildDatePairs({ tripType: s.ot, departs: departList, returns: returnList, maxCombos: 500 }),
    [s.ot, departList, returnList],
  );
  const isMultiDate = datePairs.length > 1;

  // Fan-out lotnisk po stronie klienta: dla wpisów metro/kraj rozwijamy
  // `from`/`to` na listy fizycznych IATA. Dzięki temu każde zapytanie idzie
  // do serwera jako pojedyncza para (fast-path bez serwerowego fan-outu),
  // a useQueries naturalnie renderuje wyniki PROGRESYWNIE w miarę jak
  // przeglądarka rozprasza kolejne requesty (HTTP/2 ~6–100 równoległych).
  // Cap chroni przed absurdami (kraj×kraj×30 dat = 18000 zapytań).
  const MAX_PAIRS_FANOUT = 400;
  const { pairs, truncated: pairsTruncated } = useMemo(() => {
    const fromCodes = expandMetro(s.from);
    const toCodes = expandMetro(s.to);
    const all: Array<{ depart: string; return?: string; from: string; to: string }> = [];
    for (const dp of datePairs) {
      for (const f of fromCodes) {
        for (const t of toCodes) {
          all.push({ depart: dp.depart, return: dp.return, from: f, to: t });
        }
      }
    }
    const truncated = all.length > MAX_PAIRS_FANOUT;
    return { pairs: truncated ? all.slice(0, MAX_PAIRS_FANOUT) : all, truncated };
  }, [datePairs, s.from, s.to]);
  const truncated = dateTruncated || pairsTruncated;

  const fn = useServerFn(searchFlights);
  const queryClient = useQueryClient();
  // SEARCH_TIMEOUT_MS pochodzi z @/lib/search-timing — wspólne źródło prawdy
  // dla klienta i serwera (zob. TOTAL_BUDGET_MS w flights.functions).

  // Stan anulowania bieżącego wyszukiwania. Po kliknięciu "Anuluj" wszystkie
  // jeszcze niezakończone zapytania zostają zatrzymane (cancelQueries +
  // abortSignal w queryFn), a `enabled: !canceled` blokuje useQueries przed
  // wystartowaniem KOLEJNYCH zapytań z fan-outu (np. tych, których
  // przeglądarka jeszcze nie zdążyła odpalić w paczce HTTP/2). Reset
  // następuje gdy klucz wyszukiwania się zmienia (nowe parametry w URL).
  const cancelKey = useMemo(
    () => JSON.stringify({ ...baseParams, dd: s.dd, rd: s.rd }),
    [baseParams, s.dd, s.rd],
  );
  const [canceledFor, setCanceledFor] = useState<string | null>(null);
  const canceled = canceledFor === cancelKey;

  const queries = useQueries({
    queries: pairs.map((pair) => {
      const params = {
        ...baseParams,
        from: pair.from,
        to: pair.to,
        departDate: pair.depart,
        returnDate: pair.return,
      };
      return {
        queryKey: ["flights", cancelKey, params] as const,
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          let timer: ReturnType<typeof setTimeout> | undefined;
          const timeout = new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error("Wyszukiwanie trwa zbyt długo. Sprawdź połączenie i spróbuj ponownie.")),
              SEARCH_TIMEOUT_MS,
            );
          });
          // Jeśli już anulowano (np. signal przyszedł od razu z cancelQueries),
          // nie strzelamy w ogóle do serwera — wyrzucamy AbortError, który
          // React Query rozpozna jako anulowanie (nie błąd użytkownika).
          if (signal.aborted) {
            if (timer) clearTimeout(timer);
            throw new DOMException("Aborted", "AbortError");
          }
          const abortP = new Promise<never>((_, reject) => {
            signal.addEventListener(
              "abort",
              () => reject(new DOMException("Aborted", "AbortError")),
              { once: true },
            );
          });
          try {
            // Przekazujemy `signal` do serwer-funkcji, żeby React Query
            // mógł realnie anulować HTTP request (nie tylko lokalnie
            // odrzucić Promise). Dzięki temu, gdy użytkownik klika kolejne
            // landing-page'y i URL się zmienia, stare zapytania do API są
            // natychmiast przerywane na poziomie sieci — nie czekają aż
            // serwer skończy mielić, nie blokują slotów HTTP/2 i nie
            // wracają z eSky tylko po to, by trafić do kosza.
            const res = await Promise.race([fn({ data: params, signal }), timeout, abortP]);
            // #3 — persist sukcesów do sessionStorage, żeby po hard-reloadzie
            // pokazać starsze wyniki natychmiast (initialData niżej). Tylko
            // realne success'y; błędów nie cache'ujemy.
            if (res && typeof res === "object" && "ok" in res && (res as { ok: boolean }).ok) {
              try { saveFlightResult(params, res); } catch { /* ignore */ }
            }
            return res;
          } finally {
            if (timer) clearTimeout(timer);
          }
        },
        enabled: pairs.length > 0 && !canceled,
        refetchOnWindowFocus: false,
        staleTime: 60_000,
        retry: 1,
        retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 4000),
        // #3 — initialData z sessionStorage: po hard-reloadzie React Query
        // od razu rysuje stare wyniki i równolegle leci refetch w tle.
        // `initialDataUpdatedAt` sprawia, że jeśli cache jest świeższy niż
        // `staleTime`, refetch nie ruszy w ogóle (oszczędzamy fan-out).
        initialData: () => {
          const hit = loadFlightResult(params);
          return hit?.data as never | undefined;
        },
        initialDataUpdatedAt: () => loadFlightResult(params)?.at,
      };
    }),
  });

  // Liczba ciągle „w locie" zapytań — używana do telemetrii anulowań.
  // Trzymamy w ref, żeby callbacki widziały aktualną wartość bez
  // zmiany swojej tożsamości (useCallback deps).
  const inFlightRef = useRef(0);
  useEffect(() => {
    inFlightRef.current = queries.filter((q) => q.isFetching).length;
  });

  const routeForTelemetry = useMemo(
    () => (s.from && s.to ? `${s.from}->${s.to}` : undefined),
    [s.from, s.to],
  );

  const cancelSearch = useCallback(() => {
    const inFlight = inFlightRef.current;
    setCanceledFor(cancelKey);
    queryClient.cancelQueries({ queryKey: ["flights", cancelKey] });
    // Telemetria: user kliknął „Anuluj". Logujemy nawet gdy inFlight === 0
    // — to też sygnał (klik na ukończonym wyszukiwaniu = źle zaprojektowany UX).
    trackSearchCancel({ reason: "user", route: routeForTelemetry, inFlight });
  }, [cancelKey, queryClient, routeForTelemetry]);

  // Auto-anulowanie przy szybkim przełączaniu wyszukiwań: gdy zmienia się
  // `cancelKey` (nowe parametry w URL — np. user kliknął kolejny landing),
  // anulujemy WSZYSTKIE jeszcze niezakończone zapytania `flights` z poprzedniej
  // kombinacji. Dzięki temu nie blokujemy slotów HTTP/2 starymi requestami,
  // których wyniki i tak trafiłyby do kosza, a serwer (przez przekazany
  // `signal` w queryFn) urywa fetch do eSky w pół drogi.
  useEffect(() => {
    return () => {
      const inFlight = inFlightRef.current;
      queryClient.cancelQueries({ queryKey: ["flights", cancelKey] });
      // Telemetria tylko gdy faktycznie coś było w locie — inaczej
      // zaśmiecimy logi rerenderami unmountu po zakończonym wyszukiwaniu.
      if (inFlight > 0) {
        trackSearchCancel({ reason: "autoswitch", route: routeForTelemetry, inFlight });
      }
    };
  }, [cancelKey, queryClient, routeForTelemetry]);

  // (usunięto toast startu wyszukiwania — te same informacje pokazuje
  // już różowy baner postępu „Aktualizuję wyniki…", więc dodatkowy biały
  // dymek z Sonnera tylko duplikował treść, zwłaszcza na mobile.)

  // #2 — Throttle hard-reloadów. Gdy user F5-uje tę samą trasę w środku
  // ładowania (<5s temu wystartował identyczny fan-out), pokazujemy toast
  // i NIE blokujemy zapytań — i tak najpewniej trafią w `metroCache` na
  // serwerze. Sygnalizujemy tylko, że ciągłe odświeżanie spowalnia, bo
  // każdy reload zabija in-flight requesty i kasuje progres po stronie
  // klienta. Marker zapisujemy raz per `cancelKey` (po sprawdzeniu).
  const throttleNotifiedRef = useRef<string | null>(null);
  // Stan dla persistentnego banera „nie odświeżaj": ustawiamy kiedy ten sam
  // fan-out wystartował <30 s temu. Trzymamy timestamp pierwszego startu,
  // żeby baner sam zniknął po wygaśnięciu okna throttle.
  const [recentRestart, setRecentRestart] = useState<{ startedAt: number } | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!cancelKey || pairs.length === 0) return;
    if (throttleNotifiedRef.current === cancelKey) return;
    const recent = wasRecentlyStarted(cancelKey);
    if (recent) {
      setRecentRestart({ startedAt: Date.now() - recent.ageMs });
      toast.warning("Wyniki już się ładują — nie odświeżaj strony", {
        description:
          `Identyczne wyszukiwanie wystartowało ${Math.round(recent.ageMs / 1000)} s temu. ` +
          `Każdy F5 zabija postęp i zaczyna od zera.`,
        duration: 6000,
      });
      // Telemetria: ten cancelKey właśnie został zrestartowany — odpalamy
      // event nawet jeśli to drugie wejście w SPA bez F5 (np. dwie karty
      // odpalone w pół sekundy). Admin dostaje sygnał, który route „bije się
      // sam ze sobą".
      trackSearchRestart({
        route: routeForTelemetry,
        ageMs: recent.ageMs,
        trigger: recent.ageMs < 8_000 ? "reload" : "navigation",
      });
    } else {
      setRecentRestart(null);
    }
    markFanoutStart(cancelKey);
    throttleNotifiedRef.current = cancelKey;
  }, [cancelKey, pairs.length, routeForTelemetry]);

  // Auto-hide banera po pełnym oknie throttle (30 s od pierwszego startu).
  useEffect(() => {
    if (!recentRestart) return;
    const remainingMs = Math.max(0, 30_000 - (Date.now() - recentRestart.startedAt));
    if (remainingMs === 0) {
      setRecentRestart(null);
      return;
    }
    const t = setTimeout(() => setRecentRestart(null), remainingMs);
    return () => clearTimeout(t);
  }, [recentRestart]);


  const isLoading = queries.length > 0 && queries.every((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  // Postęp dla multi-date: ile zapytań już się zakończyło (sukces lub błąd).
  const totalQueries = queries.length;
  const completedQueries = queries.filter((q) => !q.isFetching).length;
  // Ile spośród zakończonych już zapytań padło (network error lub ok=false).
  // Pokazujemy to w liczniku postępu, ale NIE blokujemy widoku — dopóki
  // jakiekolwiek zapytanie zwróci wyniki, renderujemy je częściowo.
  const failedQueries = queries.filter(
    (q) => !q.isFetching && (q.isError || (q.data && !q.data.ok)),
  ).length;
  // „Aktywne" pary = takie, których zapytanie już ruszyło (fetchStatus !==
  // 'idle') ALBO zdążyło się rozstrzygnąć (data/isError). Pary które wciąż
  // siedzą w kolejce HTTP/2 przeglądarki (idle, brak data) NIE liczą się
  // jako aktywne — dzięki temu komunikat „3 z 8 par aktywne" odróżnia
  // sytuację „czekamy w kolejce" od „leci po sieci / mamy wynik".
  const activeQueries = queries.filter(
    (q) => q.fetchStatus !== "idle" || q.data !== undefined || q.isError,
  ).length;
  // Anuluj ma sens tylko gdy coś jeszcze faktycznie leci po sieci i nie
  // wszystkie pary są jeszcze zakończone. Jeśli wszystkie zapytania mają już
  // wynik (sukces lub błąd) — nie ma czego anulować, blokujemy przycisk.
  const canCancel =
    !canceled &&
    isFetching &&
    totalQueries > 0 &&
    completedQueries < totalQueries;
  const allErrored = queries.length > 0 && queries.every((q) => q.isError || (q.data && !q.data.ok));
  // Stabilny klucz pary lotnisk + dat — niezależny od kolejności w `queries`.
  // Mapujemy każdą parę po tym kluczu zamiast po indeksie tablicy, żeby
  // ewentualna zmiana kolejności / dodanie nowych par w przyszłości nie
  // rozjechało progressu z faktycznymi zapytaniami.
  const pairKey = useCallback(
    (p: { from: string; to: string; depart: string; return?: string }) =>
      `${p.from}->${p.to}|${p.depart}|${p.return ?? ""}`,
    [],
  );
  const queryByPair = useMemo(() => {
    const m = new Map<string, (typeof queries)[number]>();
    pairs.forEach((p, i) => {
      // useQueries gwarantuje zachowanie kolejności wejścia — ale jawnie
      // budujemy mapę po kluczu pary, żeby reszta logiki nie polegała na
      // indeksowaniu i była odporna na refaktor `pairs`.
      const q = queries[i];
      if (q) m.set(pairKey(p), q);
    });
    return m;
  }, [pairs, queries, pairKey]);

  // Postęp w rozbiciu na wymiary: pary lotnisk (from->to) i kombinacje dat
  // (depart|return). Para/data uznawana za "sprawdzoną", gdy WSZYSTKIE jej
  // zapytania się zakończyły (sukces lub błąd — nie czekamy w nieskończoność).
  // "W trakcie" = ma jeszcze cokolwiek isFetching=true.
  const progressBreakdown = useMemo(() => {
    const airportMap = new Map<string, { done: number; total: number }>();
    const dateMap = new Map<string, { done: number; total: number }>();
    for (const p of pairs) {
      const q = queryByPair.get(pairKey(p));
      const done = q && !q.isFetching ? 1 : 0;
      const aKey = `${p.from}->${p.to}`;
      const dKey = `${p.depart}|${p.return ?? ""}`;
      const a = airportMap.get(aKey) ?? { done: 0, total: 0 };
      a.done += done; a.total += 1; airportMap.set(aKey, a);
      const d = dateMap.get(dKey) ?? { done: 0, total: 0 };
      d.done += done; d.total += 1; dateMap.set(dKey, d);
    }
    const summarize = (m: Map<string, { done: number; total: number }>) => {
      let checked = 0, inFlight = 0;
      for (const v of m.values()) {
        if (v.done >= v.total) checked++;
        else if (v.done > 0 || v.total > 0) inFlight++;
      }
      return { checked, inFlight, total: m.size };
    };
    return {
      airports: summarize(airportMap),
      dates: summarize(dateMap),
    };
  }, [pairs, queryByPair, pairKey]);

  // Live lista statusów per-para — każda para mapuje się na jeden z 4 stanów:
  //  - "czeka"     → query enabled, ale jeszcze nie ruszyło (idle, brak data)
  //  - "w toku"    → isFetching === true
  //  - "ukończono" → wynik OK (data.ok === true)
  //  - "błąd"      → isError lub data.ok === false
  // Mapa key → { status, updatedAt } przechowywana między renderami, żeby
  // móc pokazać moment ostatniej zmiany statusu danej pary (HH:MM:SS).
  // Aktualizujemy ją wewnątrz useMemo — pragmatycznie, ale bezpiecznie:
  // przy tym samym statusie zapis jest idempotentny.
  const pairStatusTsRef = useRef<Map<string, { status: "czeka" | "w toku" | "ukończono" | "błąd"; updatedAt: number }>>(new Map());
  const pairStatuses = useMemo(() => {
    type PairStatus = "czeka" | "w toku" | "ukończono" | "błąd";
    const now = Date.now();
    return pairs.map((p) => {
      const k = pairKey(p);
      const q = queryByPair.get(k);
      let status: PairStatus = "czeka";
      if (q) {
        if (q.isFetching) status = "w toku";
        else if (q.isError || (q.data && !q.data.ok)) status = "błąd";
        else if (q.data && q.data.ok) status = "ukończono";
      }
      const prev = pairStatusTsRef.current.get(k);
      let updatedAt: number;
      if (!prev || prev.status !== status) {
        updatedAt = now;
        pairStatusTsRef.current.set(k, { status, updatedAt });
      } else {
        updatedAt = prev.updatedAt;
      }
      return { from: p.from, to: p.to, depart: p.depart, return: p.return, status, updatedAt, key: k };
    });
  }, [pairs, queryByPair, pairKey]);

  // Agregacja metryk technicznych z WSZYSTKICH serverFn responses (każde
  // zapytanie = jeden fan-out po stronie serwera). Sumujemy hity/missy/retry
  // oraz konkatenujemy callMs do policzenia p50/p95/max w skali bieżącego
  // wyszukiwania (a nie pojedynczego zapytania). Łączymy histogram statusów.
  const aggMetrics = useMemo(() => {
    let cacheHits = 0, cacheMisses = 0, attempts = 0, retries = 0;
    let serverTotalMs = 0, responsesWithMetrics = 0;
    const callMs: number[] = [];
    const status: Record<string, number> = {};
    const searchIds: string[] = [];
    for (const q of queries) {
      const m = q.data?.metrics;
      if (!m) continue;
      responsesWithMetrics++;
      cacheHits += m.cacheHits;
      cacheMisses += m.cacheMisses;
      attempts += m.attempts;
      retries += m.retries;
      serverTotalMs = Math.max(serverTotalMs, m.totalMs);
      for (const ms of m.callMs) callMs.push(ms);
      for (const [k, v] of Object.entries(m.statusHistogram)) {
        status[k] = (status[k] ?? 0) + v;
      }
      if (m.searchId) searchIds.push(m.searchId);
    }
    const total = cacheHits + cacheMisses;
    const pct = (arr: number[], p: number) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      return sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))];
    };
    return {
      responsesWithMetrics,
      cacheHits,
      cacheMisses,
      hitRate: total > 0 ? cacheHits / total : 0,
      attempts,
      retries,
      callCount: callMs.length,
      p50Ms: pct(callMs, 50),
      p95Ms: pct(callMs, 95),
      maxMs: callMs.length ? Math.max(...callMs) : 0,
      avgMs: callMs.length ? Math.round(callMs.reduce((a, b) => a + b, 0) / callMs.length) : 0,
      serverTotalMs,
      statusHistogram: status,
      searchIds,
    };
  }, [queries]);

  // Lista par (lotnisko->lotnisko + daty), które nie odpowiedziały — czyli
  // zakończyły się błędem sieciowym/timeoutem lub odpowiedzią ok=false z API.
  // Pokazujemy ją w rozwijanym podsumowaniu, żeby użytkownik wiedział, które
  // konkretnie kombinacje zawiodły, zamiast tylko ogólnego komunikatu.
  const failedPairs = useMemo(() => {
    const out: Array<{
      from: string;
      to: string;
      depart: string;
      return?: string;
      reason: string;
    }> = [];
    pairs.forEach((p) => {
      const q = queryByPair.get(pairKey(p));
      if (!q || q.isFetching) return;
      let reason: string | null = null;
      if (q.isError) {
        const err = q.error;
        reason = err instanceof Error ? err.message : "Brak odpowiedzi";
      } else if (q.data && !q.data.ok) {
        const apiErr = q.data as { error?: string };
        reason = apiErr.error?.slice(0, 200) ?? "API zwróciło błąd";
      }
      if (reason) {
        out.push({ from: p.from, to: p.to, depart: p.depart, return: p.return, reason });
      }
    });
    return out;
  }, [pairs, queryByPair, pairKey]);

  // Live bucketing przyczyn braku wyników na 4 kategorie: 408 (timeout-like),
  // 429 (rate-limit), 5xx (błąd serwera eSky), inne (network/cache-err/...).
  // Liczymy po PARACH (każda padła kombinacja from→to+daty trafia do jednego
  // bucketu na bazie statusu wyłuskanego z reason). Aktualizuje się w trakcie
  // wyszukiwania, więc admin widzi w czasie rzeczywistym, dlaczego nie ma
  // wyników dla danej puli.
  const failureBuckets = useMemo(() => {
    const buckets = { "408": 0, "429": 0, "5xx": 0, "inne": 0 } as Record<"408" | "429" | "5xx" | "inne", number>;
    for (const f of failedPairs) {
      const r = f.reason.toLowerCase();
      const m = r.match(/\((\d{3})\)/) ?? r.match(/\b(\d{3})\b/);
      const code = m ? Number(m[1]) : NaN;
      if (code === 408 || r.includes("timeout") || r.includes("trwa zbyt długo")) buckets["408"]++;
      else if (code === 429 || r.includes("rate") || r.includes("too many")) buckets["429"]++;
      else if (code >= 500 && code < 600) buckets["5xx"]++;
      else buckets["inne"]++;
    }
    const total = buckets["408"] + buckets["429"] + buckets["5xx"] + buckets["inne"];
    return { ...buckets, total };
  }, [failedPairs]);

  const isError = allErrored;
  const error = queries.find((q) => q.error)?.error ?? null;
  const failureCount = queries.reduce((s, q) => s + (q.failureCount ?? 0), 0);
  // Dla błędu pokazujemy pierwszy komunikat z API (jeśli wszystkie failed).
  const firstApiError = queries.find((q) => q.data && !q.data.ok);
  const refetch = () => {
    // Jeśli użytkownik wcześniej kliknął "Anuluj", przywracamy zapytania —
    // ten klik jest jawną zgodą na ponowne strzelenie do API.
    if (canceled) setCanceledFor(null);
    queries.forEach((q) => q.refetch());
  };

  const fromA = findAirport(s.from);
  const toA = findAirport(s.to);

  // Agregacja: zbieramy Items ze WSZYSTKICH zapytań (po 1 dla single-date,
  // po N dla multi-date). Dedupujemy po FlightId — jeśli ta sama oferta wraca
  // dla 2 różnych dat, bierzemy najtańszą.
  type FlightItem = NonNullable<NonNullable<typeof queries[number]["data"]>["data"]>["Items"][number];
  const allItems = useMemo<FlightItem[]>(() => {
    const byId = new Map<string, FlightItem>();
    for (const q of queries) {
      if (!q.data?.ok) continue;
      const items: FlightItem[] = q.data.data?.Items ?? [];
      for (const it of items) {
        const existing = byId.get(it.FID);
        if (!existing || it.P < existing.P) byId.set(it.FID, it);
      }
    }
    return Array.from(byId.values());
  }, [queries]);

  // Predykaty filtra wyodrębnione, żeby dało się policzyć "co byłoby dostępne,
  // gdyby wszystkie filtry POZA jednym były aktywne" — klasyczne facetowanie.
  // Dzięki temu w dropdownie „Linia lotnicza" widać tylko linie, które
  // realnie występują w bieżącym zbiorze (po pozostałych filtrach), a nie
  // pełną listę 6 linii dla całej trasy, z których po włączeniu np. „Bagaż
  // rejestrowany" zostaje tak naprawdę tylko jedna.
  const passStops = useCallback((it: typeof allItems[number]) => {
    if (stopsFilter === "any") return true;
    const maxStops = Math.max(...it.Legs.map((l) => l.Segs.length - 1));
    if (stopsFilter === "direct") return maxStops === 0;
    return maxStops <= 1;
  }, [stopsFilter]);
  const passAirline = useCallback((it: typeof allItems[number]) =>
    airlineFilter === "all" || it.Legs[0]?.Segs[0]?.AC === airlineFilter, [airlineFilter]);
  const passVia = useCallback((it: typeof allItems[number]) => {
    if (viaFilter === "all") return true;
    return it.Legs.some((l) => l.Segs.slice(0, -1).some((seg) => seg.AAC === viaFilter));
  }, [viaFilter]);
  const passBag = useCallback((it: typeof allItems[number]) => {
    if (bagFilter === "any") return true;
    const need = bagFilter === "carry" ? allLegsHaveCarryOn : allLegsHaveCheckedIncluded;
    return need(it.Legs);
  }, [bagFilter]);
  const passPrice = useCallback((it: typeof allItems[number]) =>
    maxPrice === null || it.P <= maxPrice, [maxPrice]);

  // Lista linii lotniczych: liczona ze zbioru przefiltrowanego WSZYSTKIMI
  // filtrami poza linią (facet pattern). Dzięki temu liczba w „Wszystkie (N)"
  // odzwierciedla realnie wybieralne opcje przy aktualnych filtrach.
  const airlines = useMemo(() => {
    const set = new Set<string>();
    for (const it of allItems) {
      if (!passStops(it) || !passVia(it) || !passBag(it) || !passPrice(it)) continue;
      const ac = it.Legs[0]?.Segs[0]?.AC;
      if (ac) set.add(ac);
    }
    return Array.from(set).sort();
  }, [allItems, passStops, passVia, passBag, passPrice]);

  // Lotniska przesiadkowe: analogicznie — z `allItems` przefiltrowanych
  // wszystkim poza filtrem `via`. Bierzemy realne AAC ze środkowych segmentów
  // (faktyczne przesiadki), a nie API-owe `TransferCodes`, które zwraca pełną
  // listę dla trasy niezależnie od aktualnego zbioru ofert.
  const transferAirports = useMemo(() => {
    const merged = new Map<string, string>();
    // nameMap z API jako fallback dla ładnych nazw lotnisk, gdy lokalna baza
    // nie zna kodu.
    const apiNames = new Map<string, string>();
    for (const q of queries) {
      if (!q.data?.ok) continue;
      for (const n of q.data.data?.SearchFilters?.AirportsNames ?? []) {
        if (!apiNames.has(n.Key)) apiNames.set(n.Key, n.Value);
      }
    }
    for (const it of allItems) {
      if (!passStops(it) || !passAirline(it) || !passBag(it) || !passPrice(it)) continue;
      for (const l of it.Legs) {
        for (const seg of l.Segs.slice(0, -1)) {
          const code = seg.AAC;
          if (!code || merged.has(code)) continue;
          const fa = findAirport(code);
          merged.set(code, fa?.city ?? apiNames.get(code) ?? code);
        }
      }
    }
    return Array.from(merged.entries()).map(([code, label]) => ({ code, label }));
  }, [allItems, queries, passStops, passAirline, passBag, passPrice]);

  // Banner partial-success: część par lotnisk z fan-outu metro nie odpowiedziała
  // (lub przy multi-date — część zapytań dat padła), ale mamy wyniki z reszty.
  // Liczymy zarówno pary które padły z błędem, jak i te które zostały
  // anulowane przez grace-timer (patrz useEffect niżej) — z punktu widzenia
  // użytkownika to ten sam efekt: „nie zdążyły odpowiedzieć szybko".
  const partialWarning = useMemo<string | null>(() => {
    if (allErrored) return null; // pełny błąd obsługujemy osobno przez ErrorState
    const okNow = queries.filter((q) => !q.isFetching && q.data && q.data.ok).length;
    const stillFetchingNow = queries.filter((q) => q.isFetching).length;
    const missingNow = queries.length - okNow - stillFetchingNow;
    if (okNow === 0 || missingNow <= 0) return null;
    const unit = isMultiDate && datePairs.length === queries.length ? "kombinacji dat" : "par tras";
    return `Sprawdziliśmy ${okNow} z ${queries.length} ${unit} — ${missingNow} nie odpowiedziało chwilowo.`;
  }, [queries, allErrored, isMultiDate, datePairs.length]);

  // Grace-after-first-results: po pierwszych wynikach dajemy reszcie wiszących
  // zapytań krótkie okno i potem CHOWAMY pasek postępu — ale nie anulujemy
  // requestów. Wolne pary (np. WAW->FCO ~9-13s w eSky) dokończą się w tle i
  // dosypią wyniki bez "wieszania" UI. Anulowanie powodowało, że ucinaliśmy
  // pary tuż przed metą i traktowaliśmy je jako "nie odpowiedziały".
  const GRACE_AFTER_FIRST_RESULTS_MS = 8000;
  const hasAnyResults = allItems.length > 0;
  const anyStillFetching = queries.some((q) => q.isFetching);
  const [graceElapsed, setGraceElapsed] = useState(false);
  useEffect(() => {
    setGraceElapsed(false);
  }, [cancelKey]);
  useEffect(() => {
    if (canceled) return;
    if (!hasAnyResults || !anyStillFetching) return;
    const t = setTimeout(() => setGraceElapsed(true), GRACE_AFTER_FIRST_RESULTS_MS);
    return () => clearTimeout(t);
  }, [hasAnyResults, anyStillFetching, canceled, cancelKey]);
  // Effective isFetching dla UI — po grace traktujemy zapytania jako "ciche",
  // więc pasek postępu znika, ale dane wciąż mogą dochodzić w tle.
  const isFetchingVisible = isFetching && !(graceElapsed && hasAnyResults);

  // Granice cen do podpowiedzi w UI filtra
  const priceRange = useMemo(() => {
    if (allItems.length === 0) return { min: 0, max: 0 };
    let min = Infinity, max = -Infinity;
    for (const it of allItems) { if (it.P < min) min = it.P; if (it.P > max) max = it.P; }
    return { min: Math.floor(min), max: Math.ceil(max) };
  }, [allItems]);

  // Filtry → sortowanie → paginacja
  const filtered = useMemo(() => {
    let arr = allItems.filter((it) => {
      // Stopnie
      if (stopsFilter !== "any") {
        const maxStops = Math.max(...it.Legs.map((l) => l.Segs.length - 1));
        if (stopsFilter === "direct" && maxStops !== 0) return false;
        if (stopsFilter === "1stop" && maxStops > 1) return false;
      }
      // Linia lotnicza
      if (airlineFilter !== "all" && it.Legs[0]?.Segs[0]?.AC !== airlineFilter) return false;
      // Lotnisko przesiadkowe — szukamy `via` w AAC dowolnego segmentu poza
      // ostatnim w danym legu (czyli faktyczna przesiadka, nie cel końcowy).
      if (viaFilter !== "all") {
        const hits = it.Legs.some((l) =>
          l.Segs.slice(0, -1).some((seg) => seg.AAC === viaFilter),
        );
        if (!hits) return false;
      }
      // Bagaż w cenie
      if (bagFilter !== "any") {
        const need = bagFilter === "carry" ? allLegsHaveCarryOn : allLegsHaveCheckedIncluded;
        if (!need(it.Legs)) return false;
      }
      // Cena
      if (maxPrice !== null && it.P > maxPrice) return false;
      return true;
    });
    arr = [...arr];
    if (sort === "price-asc") arr.sort((a, b) => a.P - b.P);
    if (sort === "price-desc") arr.sort((a, b) => b.P - a.P);
    if (sort === "duration") arr.sort((a, b) => totalMinutes(a) - totalMinutes(b));
    if (sort === "soonest") arr.sort((a, b) => departTimestamp(a) - departTimestamp(b));
    if (sort === "recommended") {
      // Rekomendacja eSky: pole NQS (im wyższe, tym wyżej w rankingu eSky).
      // Oferty bez NQS lądują na końcu, posortowane po cenie.
      arr.sort((a, b) => {
        const an = a.NQS ?? Number.NEGATIVE_INFINITY;
        const bn = b.NQS ?? Number.NEGATIVE_INFINITY;
        if (an !== bn) return bn - an;
        return a.P - b.P;
      });
    }
    return arr;
  }, [allItems, stopsFilter, airlineFilter, viaFilter, bagFilter, maxPrice, sort]);

  // Reset strony tylko przy zmianie maxPrice (slider). Pozostałe filtry
  // (stops/airline/via/bag/sort) resetują stronę w swoich setterach.
  // CELOWO nie reagujemy na `allItems.length` — gdy w trakcie streamingu
  // dochodzą nowe wyniki, NIE chcemy wybijać użytkownika ze strony, na
  // której właśnie jest. `filtered` i tak re-sortuje cały zbiór, więc
  // nowe oferty trafią we właściwe miejsce na liście.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [maxPrice]);

  // Ile ofert „mieści się" w kolejnych stronach: 25, 75, 125, 175…
  // totalPages wyliczamy odwrotnie: znajdź najmniejsze p, dla którego
  // visibleCountForPage(p) >= filtered.length.
  const totalPages = filtered.length <= INITIAL_PAGE_SIZE
    ? 1
    : 1 + Math.ceil((filtered.length - INITIAL_PAGE_SIZE) / PAGE_STEP);
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice(0, visibleCountForPage(safePage));
  const hasMore = safePage < totalPages;

  // Auto-load przy doscrollowaniu do końca listy (jak Skyscanner/Google
  // Flights). Sentinel renderowany pod listą — gdy wjedzie w viewport
  // (rootMargin=400px → load z wyprzedzeniem), zwiększamy stronę.
  // Przycisk „Pokaż więcej" zostaje jako fallback dla a11y / no-JS.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!hasMore) return;
    const el = loadMoreRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setPage(Math.min(safePage + 1, totalPages));
        }
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, totalPages, safePage]);

  const resetFilters = () => {
    navigate({ search: (prev: typeof s) => ({ ...prev, stops: "any", airline: "all", via: "all", bag: "any", sort: "price-asc", page: 1 }) });
    setMaxPrice(null);
  };
  const hasActiveFilters = stopsFilter !== "any" || airlineFilter !== "all" || viaFilter !== "all" || bagFilter !== "any" || maxPrice !== null;

  // Live „state" do panelu admina: ile wyników user widzi w danym momencie,
  // czy nadal szukamy mimo pustki, czy padło wszystko z błędem. Push do
  // analytics_events na ścieżkę `/_state/search` (admin filtruje ze stats,
  // ale używa do wzbogacenia karty Live ścieżki). Throttling po stronie
  // analytics.ts — tu po prostu wołamy przy każdej zmianie stanu.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!s.from || !s.to || !s.dd) return;
    let status: "loading" | "results" | "empty" | "error";
    if (allErrored) status = "error";
    else if (filtered.length > 0) status = "results";
    else if (isFetching || queries.length === 0) status = "loading";
    else status = "empty";
    let errorMsg: string | null = null;
    if (status === "error") {
      if (error instanceof Error) errorMsg = error.message.slice(0, 160);
      else {
        const apiErr = firstApiError?.data as { ok?: boolean; error?: string } | undefined;
        errorMsg = apiErr?.error?.slice(0, 160) ?? "Brak odpowiedzi z API.";
      }
    }
    // Kompaktowy string z info o ograniczeniach widocznych dla usera
    // (badge „sprawdzamy 30 z 42 wariantów dat" / „400 par lotnisk").
    // null = user nie widzi żadnego cappingu.
    const truncatedParts: string[] = [];
    if (dateTruncated) truncatedParts.push(`${datePairs.length}/${dateTotal} dat`);
    if (pairsTruncated) truncatedParts.push(`${MAX_PAIRS_FANOUT}/${pairs.length}+ par`);
    const truncatedInfo = truncatedParts.length > 0 ? truncatedParts.join(" · ") : null;
    import("@/lib/analytics").then(({ trackSearchState }) => {
      trackSearchState({
        visible: filtered.length,
        status,
        route: `${s.from}→${s.to}`,
        pairs: pairs.length,
        errorMsg,
        partial: partialWarning,
        truncated: truncatedInfo,
      });
    }).catch(() => {});
  }, [filtered.length, isFetching, allErrored, queries.length, pairs.length, s.from, s.to, s.dd, error, firstApiError, partialWarning, dateTruncated, dateTotal, datePairs.length, pairsTruncated, MAX_PAIRS_FANOUT]);

  // Flush ostatniego pending state przy odmontowaniu / zmianie trasy,
  // żeby admin nie utknął na pośrednim „loading" gdy throttle złapał update.
  useEffect(() => {
    return () => {
      import("@/lib/analytics").then(({ flushPendingSearchState }) => flushPendingSearchState()).catch(() => {});
    };
  }, []);



  return (
    <div className="min-h-screen">
      <Header />

      <section className="container mx-auto px-4 pt-6">
        <h1 className="font-display text-3xl font-bold md:text-4xl">
          {fromA?.city ?? s.from} <span className="text-gradient-sunset">→</span> {toA?.city ?? s.to}
        </h1>
        <p className="text-muted-foreground">
          {formatDateListPl(departList)}
          {s.ot === "RoundTrip" && returnList.length > 0 && ` – ${formatDateListPl(returnList)}`}
          {" · "}{s.a + s.c + s.i} {s.a + s.c + s.i === 1 ? "pasażer" : "pasażerów"}
          {isMultiDate && (
            <span
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
              title={dateTruncated
                ? `Z ${dateTotal} możliwych wariantów dat sprawdzamy ${datePairs.length} najbardziej trafnych — żeby wyniki przyszły szybko.`
                : undefined}
            >
              {dateTruncated
                ? `sprawdzamy ${datePairs.length} z ${dateTotal} wariantów dat`
                : `${datePairs.length} kombinacji dat`}
            </span>
          )}
          {pairs.length > datePairs.length && (
            <span
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
              title={pairsTruncated
                ? `Z większej liczby par lotnisk sprawdzamy ${MAX_PAIRS_FANOUT} najbardziej trafnych.`
                : undefined}
            >
              {pairsTruncated
                ? `sprawdzamy ${MAX_PAIRS_FANOUT} par lotnisk`
                : `${pairs.length} par lotnisk`}
            </span>
          )}
        </p>

        {dateInputErrors.length > 0 && (
          <div
            role="alert"
            aria-live="polite"
            className="mt-4 flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-medium">Nieprawidłowy format dat w adresie URL</p>
              <ul className="list-inside list-disc text-destructive/90">
                {dateInputErrors.map(({ field, err }) => (
                  <li key={field}>
                    <span className="font-medium">{field}:</span> {err.message}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-destructive/80">
                Akceptowane formaty: <code>YYYY-MM-DD</code>, lista po przecinku
                (<code>YYYY-MM-DD,YYYY-MM-DD</code>) lub zakres{" "}
                <code>YYYY-MM-DD..YYYY-MM-DD</code>. Popraw daty w formularzu poniżej.
              </p>
            </div>
          </div>
        )}

        <div className="mt-5">
          <SearchForm initial={{
            tripType: s.ot, from: s.from, to: s.to,
            departDate: s.dd, returnDate: s.rd,
            adults: s.a, children: s.c, infants: s.i,
            serviceClass: s.sc, directOnly: s.d === 1,
          }} showCopyLink />
        </div>
      </section>

      <section className="container mx-auto px-4 py-8">
        <HeavyFanoutHint
          pairs={pairs.length}
          isFetching={isFetching}
          hasResults={allItems.length > 0}
          recentRestart={recentRestart}
        />
        {(() => {
          // Pojedyncze źródło prawdy o tym, który widok pokazać. Pokryte
          // testami w `src/lib/__tests__/results-view-state.test.ts` —
          // zwłaszcza scenariusze multi-date, w których nie wolno mignąć
          // EmptyState, dopóki cokolwiek leci.
          const view = decideResultsView({
            totalQueries,
            completedQueries,
            itemCount: allItems.length,
            allErrored,
          });
          // ErrorState ma dodatkową ścieżkę: API zwraca 200 + ok=false na
          // pierwszym zapytaniu i nie mamy żadnych wyników — wtedy też
          // pokazujemy pełnoekranowy błąd, nie czekamy na resztę.
          const apiHardError =
            !!firstApiError && firstApiError.data && !firstApiError.data.ok && allItems.length === 0;
          if (view === "error" || apiHardError) {
            return (
              <ErrorState
                apiMessage={firstApiError?.data && !firstApiError.data.ok ? firstApiError.data.error : null}
                errorMessage={error instanceof Error ? error.message : null}
                onRetry={() => refetch()}
                isRetrying={isFetching}
                attempts={failureCount}
              />
            );
          }
          if (canceled && allItems.length === 0) {
            // Anulowano przed pierwszym wynikiem — pokazujemy jawny komunikat
            // zamiast wiecznego spinnera (canceled => enabled:false, więc
            // useQueries nigdy nie wystartuje pozostałych zapytań).
            return (
              <CanceledState
                completed={completedQueries}
                total={totalQueries}
                onRetry={refetch}
              />
            );
          }
          if (view === "loading" || view === "partial-loading") {
            // Jeden, ujednolicony widok ładowania w stylu paska postępu —
            // ten sam komponent obsługuje pierwsze ładowanie i background
            // refetch, żeby UI był spójny i nie migał między dwoma stylami.
            return (
              <>
                <SortChipsRow sort={sort} setSort={setSort} disabled={allItems.length === 0 && completedQueries === 0} />
                <RefetchingBanner
                  isFetching
                  completed={completedQueries}
                  total={totalQueries}
                  failed={failedQueries}
                  pairs={pairs.length}
                  activePairs={activeQueries}
                  breakdown={progressBreakdown}
                />
                <FlightResultsSkeleton count={6} />
              </>
            );
          }
          if (view === "empty") {
            return (
              <EmptyState
                hasActiveFilters={hasActiveFilters}
                onResetFilters={resetFilters}
                onRetry={() => refetch()}
                isRetrying={isFetching}
                directOnly={s.d === 1}
                onDisableDirect={() => navigate({ search: (prev: typeof s) => ({ ...prev, d: 0, page: 1 }) })}
              />
            );
          }
          return null;
        })()}
        {decideResultsView({
          totalQueries,
          completedQueries,
          itemCount: allItems.length,
          allErrored,
        }) === "results" && (
          <>
            {/* Background refetch (mamy już dane, ale React Query odświeża w
                tle, np. po staleTime) — dyskretny pasek na górze, nie chowamy
                istniejących wyników. */}
            <RefetchingBanner isFetching={isFetchingVisible} completed={completedQueries} total={totalQueries} failed={failedQueries} pairs={pairs.length} activePairs={activeQueries} breakdown={progressBreakdown} />
            {partialWarning && (
              <PartialResultsBanner message={partialWarning} onRetry={() => refetch()} isRetrying={isFetching} />
            )}
            {/* Sortowanie */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Sortuj:</span>
              <SortChip active={sort === "recommended"} onClick={() => setSort("recommended")}>
                Polecane
              </SortChip>
              <SortChip active={sort === "price-asc"} onClick={() => setSort("price-asc")}>
                <ArrowUp className="h-3 w-3" /> Najtaniej
              </SortChip>
              <SortChip active={sort === "price-desc"} onClick={() => setSort("price-desc")}>
                <ArrowDown className="h-3 w-3" /> Najdrożej
              </SortChip>
              <SortChip active={sort === "soonest"} onClick={() => setSort("soonest")}>
                Najszybciej
              </SortChip>
              <SortChip active={sort === "duration"} onClick={() => setSort("duration")}>
                Najkrócej
              </SortChip>
              <div className="ml-auto flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  {filtered.length === allItems.length
                    ? <>Znaleziono {filtered.length} {filtered.length === 1 ? "wynik" : "wyników"}</>
                    : <>{filtered.length} z {allItems.length} po filtrach</>}
                </span>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground"
                >
                  Wyczyść
                </Link>
              </div>
            </div>


            {/* Filtry */}
            <div className="glass mb-6 grid gap-3 rounded-lg p-4 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
              {s.d !== 1 && (
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Przesiadki
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    <SortChip active={stopsFilter === "any"} onClick={() => setStopsFilter("any")}>Wszystkie</SortChip>
                    <SortChip active={stopsFilter === "direct"} onClick={() => setStopsFilter("direct")}>Bezpośrednie</SortChip>
                    <SortChip active={stopsFilter === "1stop"} onClick={() => setStopsFilter("1stop")}>Max 1 przesiadka</SortChip>
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="flt-airline" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Linia lotnicza
                </label>
                <select
                  id="flt-airline"
                  value={airlineFilter}
                  onChange={(e) => setAirlineFilter(e.target.value)}
                  className="w-auto max-w-full rounded-md border border-white/10 bg-background/50 px-3 py-2 text-sm"
                >
                  <option value="all">Wszystkie ({airlines.length})</option>
                  {airlines
                    .map((a) => ({ code: a, name: airlineName(a) }))
                    .sort((x, y) => x.name.localeCompare(y.name, "pl"))
                    .map(({ code, name }) => (
                      <option key={code} value={code}>{name === code ? code : `${name} (${code})`}</option>
                    ))}
                </select>
              </div>
              {transferAirports.length > 0 && (
                <div>
                  <label htmlFor="flt-via" className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <span>Lotnisko przesiadkowe</span>
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="Co to znaczy?"
                            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          >
                            <HelpCircle className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs leading-snug">
                          Filtruje po lotnisku, na którym masz <strong>przesiadkę</strong> (np. Frankfurt w drodze do Londynu) — nie po lotnisku docelowym. Wybierz, jeśli chcesz przesiadać się tylko na konkretnym hubie.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </label>
                  <select
                    id="flt-via"
                    value={viaFilter}
                    onChange={(e) => setViaFilter(e.target.value)}
                    title="Filtr dotyczy lotniska przesiadkowego, nie celu podróży"
                    className="w-auto max-w-full rounded-md border border-white/10 bg-background/50 px-3 py-2 text-sm"
                  >
                    <option value="all">Dowolne ({transferAirports.length})</option>
                    {transferAirports
                      .slice()
                      .sort((a, b) => a.label.localeCompare(b.label, "pl"))
                      .map(({ code, label }) => (
                        <option key={code} value={code}>{label} ({code})</option>
                      ))}
                  </select>
                </div>
              )}
              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span id="flt-bag-label" className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Bagaż w cenie
                  </span>
                  {bagFilter !== "any" && (
                    <button
                      type="button"
                      onClick={() => setBagFilter("any")}
                      className="text-xs text-primary hover:underline"
                      title="Wyczyść filtr bagażu (bag=any)"
                    >
                      wyczyść
                    </button>
                  )}
                </div>
                <div
                  role="radiogroup"
                  aria-labelledby="flt-bag-label"
                  className="flex flex-wrap gap-1.5"
                >
                  <SortChip active={bagFilter === "any"} onClick={() => setBagFilter("any")}>
                    Dowolny
                  </SortChip>
                  <SortChip active={bagFilter === "carry"} onClick={() => setBagFilter("carry")}>
                    <Briefcase className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                    Podręczny
                  </SortChip>
                  <SortChip active={bagFilter === "checked"} onClick={() => setBagFilter("checked")}>
                    <Luggage className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                    Rejestrowany
                  </SortChip>
                </div>
              </div>
              <div>
                <label htmlFor="flt-price" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Maks. cena: {maxPrice ?? priceRange.max} zł
                </label>
                <Slider
                  id="flt-price"
                  min={priceRange.min}
                  max={priceRange.max}
                  step={Math.max(10, Math.round((priceRange.max - priceRange.min) / 50))}
                  value={[maxPrice ?? priceRange.max]}
                  onValueChange={(values) => setMaxPrice(values[0] ?? priceRange.max)}
                  className="py-2"
                />
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-1 text-xs text-primary hover:underline"
                  >
                    Wyczyść filtry
                  </button>
                )}
              </div>
            </div>

            {/* Wyniki */}
            {pageItems.length === 0 ? (
              <div className="glass rounded-lg p-8 text-center">
                <p className="text-muted-foreground">Brak lotów spełniających wybrane filtry.</p>
                <Button onClick={resetFilters} variant="glass" className="mt-4">Wyczyść filtry</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {pageItems.map((item) => (
                  <FlightCard key={item.FID} item={item} logoSize={logoSize} />
                ))}
              </div>
            )}

            {/* Sentinel do auto-load przy doscrollowaniu do końca listy.
                IntersectionObserver (loadMoreRef) wykrywa wejście w viewport
                i auto-zwiększa stronę. Przycisk zostaje jako fallback dla
                a11y / no-JS / klawiatury. */}
            {hasMore && (
              <>
                <div ref={loadMoreRef} aria-hidden="true" className="h-1 w-full" />
                <div className="mt-8 text-center">
                  <Button
                    variant="glass"
                    size="lg"
                    onClick={() => setPage(safePage + 1)}
                  >
                    Pokaż więcej wyników ({filtered.length - pageItems.length} pozostało)
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {failedPairs.length > 0 && (
          <FailedPairsDetails failed={failedPairs} totalPairs={pairs.length} />
        )}

        {/* (usunięto sekcję „Szczegóły techniczne" z widoku wyszukiwania —
            metryki cache / retry / latency są nadal liczone na backendzie
            i widoczne w adminie, ale dla użytkownika końcowego to szum.) */}
      </section>

      {fromA && toA && (
        <FaqSection
          faq={buildSearchFaq({
            fromCity: fromA.city, toCity: toA.city,
            fromCode: fromA.code, toCode: toA.code,
          })}
        />
      )}

      <Footer />
    </div>
  );
}

function FaqSection({ faq }: { faq: { q: string; a: string }[] }) {
  return (
    <section className="container mx-auto px-4 pb-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-primary" />
          <h2 className="font-display text-2xl font-bold md:text-3xl">Najczęstsze pytania</h2>
        </div>
        <div className="space-y-3">
          {faq.map((f, i) => (
            <details
              key={i}
              className="glass group rounded-lg p-4 [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-3 font-semibold">
                <span>{f.q}</span>
                <span className="text-primary transition-transform group-open:rotate-45" aria-hidden>+</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pagination({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) {
  // Strony do wyświetlenia: 1 … (page-1) page (page+1) … last
  const pages: (number | "…")[] = [];
  const add = (n: number) => { if (!pages.includes(n) && n >= 1 && n <= totalPages) pages.push(n); };
  add(1);
  if (page - 1 > 2) pages.push("…");
  for (let p = page - 1; p <= page + 1; p++) add(p);
  if (page + 1 < totalPages - 1) pages.push("…");
  add(totalPages);

  const handleClick = (p: number) => (e: React.MouseEvent) => {
    // Pozwól przeglądarce obsłużyć cmd/ctrl-click (otwarcie w nowej karcie)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    onPage(p);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Zachowuje WSZYSTKIE pozostałe parametry URL (filtry, daty, pasażerów…)
  const linkSearch = (p: number) => ((prev: Record<string, unknown>) => ({ ...prev, page: p })) as never;

  const prevDisabled = page === 1;
  const nextDisabled = page === totalPages;

  return (
    <nav aria-label="Paginacja wyników" className="mt-8 flex flex-wrap items-center justify-center gap-2">
      {prevDisabled ? (
        <Button variant="glass" size="sm" disabled aria-label="Poprzednia strona">← Poprzednia</Button>
      ) : (
        <Button asChild variant="glass" size="sm">
          <Link
            to="/search"
            search={linkSearch(page - 1)}
            onClick={handleClick(page - 1)}
            aria-label={`Przejdź do strony ${page - 1} (poprzednia)`}
            rel="prev"
          >
            ← Poprzednia
          </Link>
        </Button>
      )}

      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e-${i}`} className="px-2 text-muted-foreground" aria-hidden="true">…</span>
        ) : (
          <Link
            key={p}
            to="/search"
            search={linkSearch(p)}
            onClick={handleClick(p)}
            aria-current={p === page ? "page" : undefined}
            aria-label={p === page ? `Bieżąca strona, strona ${p}` : `Przejdź do strony ${p}`}
            className={`min-w-[2.25rem] rounded-md px-3 py-1.5 text-center text-sm font-medium transition ${
              p === page ? "bg-gradient-sunset text-primary-foreground shadow-glow" : "glass text-foreground hover:text-primary"
            }`}
          >
            {p}
          </Link>
        )
      )}

      {nextDisabled ? (
        <Button variant="glass" size="sm" disabled aria-label="Następna strona">Następna →</Button>
      ) : (
        <Button asChild variant="glass" size="sm">
          <Link
            to="/search"
            search={linkSearch(page + 1)}
            onClick={handleClick(page + 1)}
            aria-label={`Przejdź do strony ${page + 1} (następna)`}
            rel="next"
          >
            Następna →
          </Link>
        </Button>
      )}
    </nav>
  );
}


function totalMinutes(item: { Legs: { FT: string }[] }) {
  return item.Legs.reduce((sum, l) => {
    const [h, m] = l.FT.split(":").map(Number);
    return sum + (h * 60 + (m || 0));
  }, 0);
}

function departTimestamp(item: { Legs: { Segs: { DD: string }[] }[] }) {
  const dd = item.Legs[0]?.Segs[0]?.DD;
  if (!dd) return Number.MAX_SAFE_INTEGER;
  const m = dd.match(/\/Date\((\d+)\)\//);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

function SortChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition ${
        active ? "bg-gradient-sunset text-primary-foreground shadow-glow" : "glass text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

// Pasek z chipami sortowania — używany zarówno nad listą wyników, jak i nad
// LoadingState (żeby użytkownik mógł wybrać kolejność JUŻ w trakcie ładowania;
// streamujące się wyniki wskakują automatycznie we właściwe miejsce).
function SortChipsRow({
  sort,
  setSort,
  disabled = false,
}: {
  sort: Sort;
  setSort: (v: Sort) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={`mb-3 flex flex-wrap items-center gap-2 ${disabled ? "opacity-70" : ""}`}
      aria-label="Sortowanie wyników"
    >
      <Filter className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">Sortuj:</span>
      <SortChip active={sort === "recommended"} onClick={() => setSort("recommended")}>
        Polecane
      </SortChip>
      <SortChip active={sort === "price-asc"} onClick={() => setSort("price-asc")}>
        <ArrowUp className="h-3 w-3" /> Najtaniej
      </SortChip>
      <SortChip active={sort === "price-desc"} onClick={() => setSort("price-desc")}>
        <ArrowDown className="h-3 w-3" /> Najdrożej
      </SortChip>
      <SortChip active={sort === "soonest"} onClick={() => setSort("soonest")}>
        Najszybciej
      </SortChip>
      <SortChip active={sort === "duration"} onClick={() => setSort("duration")}>
        Najkrócej
      </SortChip>
    </div>
  );
}

type ProgressBreakdown = {
  airports: { checked: number; inFlight: number; total: number };
  dates: { checked: number; inFlight: number; total: number };
};

// Mała ikonka z tooltipem wyjaśniająca skąd bierze się stan
// „czekamy w kolejce" / „N w kolejce" — to NIE jest błąd ani zacięcie,
// tylko naturalny limit przeglądarki na liczbę równoległych
// połączeń HTTP/2 do jednego hosta. Współdzielona przez LoadingState
// i RefetchingBanner, żeby brzmienie wyjaśnienia było identyczne.
function QueueHintTooltip() {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Co znaczy w kolejce?"
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-pink-300/70 transition hover:bg-pink-500/15 hover:text-pink-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-pink-400"
          >
            <HelpCircle className="h-3 w-3" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
          To nie błąd — przeglądarka ogranicza liczbę równoległych połączeń
          HTTP/2 do jednego hosta, więc część par tras chwilę czeka, aż
          wcześniejsze zapytania się zwolnią. Pary z kolejki ruszą same,
          gdy tylko będzie miejsce.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Persistentny baner ostrzegający użytkownika, że ciężki fan-out (≥8 par
 * tras — czyli typowo metro→cały kraj) wymaga cierpliwości, ORAZ że jeśli
 * właśnie zrestartował identyczne wyszukiwanie (<30 s temu), kolejny F5
 * tylko pogorszy sprawę. Nie blokujemy zapytań (serwerowy metroCache i tak
 * deduplikuje równoległe fan-outy), ale komunikat musi być widoczny stale,
 * a nie tylko jako jednorazowy toast — bo zniknięty toast nie powstrzymuje
 * nerwowego F5 po 15 s czekania.
 */
function HeavyFanoutHint({
  pairs,
  isFetching,
  hasResults,
  recentRestart,
}: {
  pairs: number;
  isFetching: boolean;
  hasResults: boolean;
  recentRestart: { startedAt: number } | null;
}) {
  // Pokazujemy tylko gdy realnie coś leci i jeszcze nie ma wyników. Po
  // pojawieniu się pierwszego wyniku user widzi karty i nie potrzebuje już
  // ostrzeżenia — baner znika.
  if (!isFetching || hasResults) return null;
  const heavy = pairs >= 8;
  if (!heavy && !recentRestart) return null;

  const title = recentRestart
    ? "Zostań na stronie — F5 zabija postęp"
    : "Zostań na stronie — to może chwilę potrwać";

  const description = recentRestart
    ? `Identyczne wyszukiwanie wystartowało ${Math.max(
        1,
        Math.round((Date.now() - recentRestart.startedAt) / 1000),
      )} s temu. Każde odświeżenie kasuje to, co już się załadowało, i zaczyna od zera. Daj wynikom maks. ~60 s.`
    : `Sprawdzamy ${pairs} par tras (metro → cały region). Wyniki spływają stopniowo i pełen fan-out może potrwać do ~60 s. Nie odświeżaj strony — każdy reload zabija postęp.`;

  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-4 flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <p className="text-amber-100/80">{description}</p>
      </div>
    </div>
  );
}


function LoadingState({
  completed = 0,
  total = 0,
  failed = 0,
  variant = "loading",
  estimatedSec,
  pairs = 1,
  activePairs,
  breakdown,
  pairStatuses,
  onCancel,
}: {
  completed?: number;
  total?: number;
  failed?: number;
  variant?: "loading" | "partial-loading";
  estimatedSec?: number;
  pairs?: number;
  activePairs?: number;
  breakdown?: ProgressBreakdown;
  pairStatuses?: Array<{ from: string; to: string; depart: string; return?: string; status: "czeka" | "w toku" | "ukończono" | "błąd"; updatedAt?: number; key: string }>;
  onCancel?: () => void;
}) {
  const showProgress = total > 1;
  
  const pct = showProgress ? Math.round((completed / total) * 100) : 0;
  const headline =
    variant === "partial-loading"
      ? "Dociągamy pozostałe kombinacje dat..."
      : "Szukamy najlepszych ofert dla Ciebie...";
  const progressLabel = showProgress
    ? failed > 0
      ? `${completed} z ${total} zapytań zakończonych (${failed} z błędem)`
      : `${completed} z ${total} zapytań zakończonych`
    : "";
  // Licznik upływającego czasu od startu wyszukiwania — pokazywany ZAWSZE
  // podczas ładowania (nawet dla pojedynczej pary), żeby użytkownik wiedział,
  // ile sesja już trwa. ETA dokładamy tylko, jeśli mamy historyczną estymatę
  // i więcej niż 1 parę (dla 1 pary i tak idzie szybko).
  const showEta = !!estimatedSec && pairs > 1 && variant === "loading";
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (variant !== "loading" && variant !== "partial-loading") return;
    const t0 = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 250);
    return () => clearInterval(id);
  }, [variant]);
  const remaining = showEta ? Math.max(0, (estimatedSec ?? 0) - elapsed) : 0;
  // Gdy część par jeszcze nie ruszyła (np. siedzą w kolejce HTTP/2),
  // pokazujemy bardziej precyzyjnie „N z M par aktywne" zamiast samego „M par tras".
  // Brzmienie i odmiana zunifikowane z RefetchingBanner przez wspólny helper.
  const pairsLabel = formatPairsStatus(pairs, activePairs);
  const timeLabel = showEta
    ? remaining > 0
      ? `${elapsed} s z ~${estimatedSec} s`
      : `${elapsed} s — kończymy...`
    : `trwa ${elapsed} s`;
  const liveLabel = pairsLabel ? `${timeLabel} · ${pairsLabel}` : timeLabel;
  // Pokazujemy ikonkę z tooltipem przy stanach „kolejka HTTP/2", żeby
  // nie wyglądało to jak błąd ani zacięcie — to normalne ograniczenie
  // przeglądarki na liczbę równoległych połączeń HTTP/2 do jednego hosta.
  const showQueueHint = pairs > 0 && activePairs != null && activePairs < pairs;
  // Historia rozmiaru kolejki w czasie — zasilana próbką przy KAŻDEJ
  // zmianie liczby aktywnych par. Limit 60 punktów żeby sparkline
  // pozostał czytelny i nie rósł w nieskończoność.
  const queueNow = activePairs != null ? Math.max(0, pairs - activePairs) : 0;
  const [queueHistory, setQueueHistory] = useState<number[]>([]);
  useEffect(() => {
    if (activePairs == null) return;
    setQueueHistory((prev) => {
      // Deduplikuj: nie zapisuj kolejnej takiej samej próbki — sparkline
      // i tak pokaże poziomą linię, a tu chcemy widzieć rzeczywiste zmiany.
      if (prev.length > 0 && prev[prev.length - 1] === queueNow) return prev;
      const next = [...prev, queueNow];
      return next.length > 60 ? next.slice(next.length - 60) : next;
    });
  }, [queueNow, activePairs]);
  // Reset historii przy starcie nowej sesji ładowania (variant flip).
  useEffect(() => {
    if (variant === "loading" || variant === "partial-loading") {
      setQueueHistory([]);
    }
  }, [variant]);
  const showSparkline = queueHistory.length >= 2 && Math.max(...queueHistory) > 0;
  // Komunikat dla czytników ekranu — jedno czyste zdanie, bez „·" / „—"
  // (które SR czyta jako „kropka"/„minus") i ze słowem „w kolejce
  // przeglądarki", żeby nie zabrzmiało jak błąd serwera.
  const ariaSentence = formatPairsStatusAria(pairs, activePairs);
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center justify-center gap-2 py-6 text-muted-foreground">
        {/* Headline jest statyczny — wystarczy raz, nie potrzebuje aria-live. */}
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
          <span>{headline}</span>
        </div>
        {/* Wizualny komunikat — z tooltipem + sparkline — ukryty dla SR,
            żeby nie powtarzać tekstu (sr-only region niżej zrobi to czyściej). */}
        <div
          className="inline-flex items-center gap-1.5 text-xs font-medium text-pink-300"
          aria-hidden="true"
        >
          <span>{liveLabel}</span>
          {showQueueHint && <QueueHintTooltip />}
          {showSparkline && <QueueSparkline samples={queueHistory} />}
        </div>
        {/* Dedykowany region aria-live tylko dla zdania o parach/kolejce.
            aria-atomic=true → SR czyta CAŁE zdanie przy każdej zmianie. */}
        <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {ariaSentence}
        </span>
        {showProgress && (
          <>
            {/* Osobny region aria-live: tylko ten licznik się zmienia w czasie,
                więc czytnik ekranu ogłasza wyłącznie nowy stan postępu, a nie
                cały baner od nowa przy każdym zakończonym zapytaniu. */}
            <div
              className={
                failed > 0
                  ? "text-xs font-medium text-amber-300"
                  : "text-xs font-medium text-pink-300"
              }
              aria-live="polite"
              aria-atomic="true"
            >
              {progressLabel}
            </div>
            <div
              className="h-1.5 w-48 overflow-hidden rounded-full bg-pink-500/15"
              role="progressbar"
              aria-label="Postęp wyszukiwania ofert"
              aria-valuenow={completed}
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuetext={progressLabel}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-pink-500 to-pink-400 transition-[width] duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </>
        )}
        {breakdown && (breakdown.airports.total > 1 || breakdown.dates.total > 1) && (
          <div
            className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground"
            aria-live="polite"
            aria-atomic="true"
          >
            {breakdown.airports.total > 1 && (
              <span>
                Pary lotnisk:{" "}
                <span className="font-medium text-foreground">
                  {breakdown.airports.checked}/{breakdown.airports.total}
                </span>
                {breakdown.airports.inFlight > 0 && (
                  <span className="text-pink-300"> · {breakdown.airports.inFlight} w trakcie</span>
                )}
              </span>
            )}
            {breakdown.dates.total > 1 && (
              <span>
                Daty:{" "}
                <span className="font-medium text-foreground">
                  {breakdown.dates.checked}/{breakdown.dates.total}
                </span>
                {breakdown.dates.inFlight > 0 && (
                  <span className="text-pink-300"> · {breakdown.dates.inFlight} w trakcie</span>
                )}
              </span>
            )}
          </div>
        )}
        {/* Statusy par tras mielą się w tle — nie pokazujemy ich nikomu (również adminowi). */}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            aria-label="Anuluj trwające wyszukiwanie"
          >
            <X className="h-3 w-3" /> Anuluj wyszukiwanie
          </button>
        )}
      </div>
      <FlightResultsSkeleton count={6} />
    </div>
  );
}

// Pasek widoczny przy background refetch (mamy już wyniki, ale React Query
// pobiera świeższą wersję). Nie zasłania listy — informuje, że dane się
// aktualizują.
// Live lista statusów dla każdej pary trasy — pokazujemy w trakcie ładowania,
// żeby użytkownik (i admin) widział na bieżąco, które pary już się rozstrzygnęły,
// które jeszcze lecą po sieci, a które wciąż siedzą w kolejce przeglądarki.
// Domyślnie schowana w <details>, żeby nie zaśmiecać widoku przy dziesiątkach par.
function PairStatusList({
  items,
}: {
  items: Array<{ from: string; to: string; depart: string; return?: string; status: "czeka" | "w toku" | "ukończono" | "błąd"; updatedAt?: number; key: string }>;
}) {
  // Stały formatter HH:MM:SS (24h, locale PL) — wynosimy poza render listy.
  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat("pl-PL", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }),
    [],
  );
  const counts = items.reduce(
    (acc, it) => {
      acc[it.status]++;
      return acc;
    },
    { "czeka": 0, "w toku": 0, "ukończono": 0, "błąd": 0 } as Record<"czeka" | "w toku" | "ukończono" | "błąd", number>,
  );
  const dot: Record<typeof items[number]["status"], string> = {
    "czeka": "bg-muted-foreground/40",
    "w toku": "bg-pink-400 animate-pulse",
    "ukończono": "bg-emerald-400",
    "błąd": "bg-amber-400",
  };
  // Auto-rozwijanie: gdy pojawi się pierwszy status „w toku" lub „błąd",
  // panel sam się rozwija; gdy wszystko jest „ukończono" — zwija się
  // z powrotem. Ręczne kliknięcie użytkownika ma pierwszeństwo i wyłącza
  // dalsze auto-sterowanie do końca życia komponentu.
  const hasActiveOrError = counts["w toku"] > 0 || counts["błąd"] > 0;
  const allDone = items.length > 0 && counts["ukończono"] === items.length;
  const [open, setOpen] = useState(false);
  const userPinnedRef = useRef(false);
  useEffect(() => {
    if (userPinnedRef.current) return;
    if (hasActiveOrError) setOpen(true);
    else if (allDone) setOpen(false);
  }, [hasActiveOrError, allDone]);
  return (
    <details
      className="mt-2 w-full max-w-md text-[11px]"
      open={open}
      onToggle={(e) => {
        const next = (e.currentTarget as HTMLDetailsElement).open;
        // Jeżeli stan w DOM różni się od naszego, znaczy że to klik
        // użytkownika (a nie nasz programowy update) — zapamiętujemy
        // wybór i wyłączamy auto-sterowanie.
        if (next !== open) {
          userPinnedRef.current = true;
          setOpen(next);
        }
      }}
    >
      <summary className="cursor-pointer select-none text-muted-foreground hover:text-foreground">
        Statusy par tras:{" "}
        <span className="font-medium text-foreground">
          {counts["ukończono"]} ukończono · {counts["w toku"]} w toku · {counts["czeka"]} czeka · {counts["błąd"]} błąd
        </span>
      </summary>
      <ul
        className="mt-1.5 max-h-48 space-y-0.5 overflow-y-auto rounded border border-border bg-background/40 p-2"
        aria-live="polite"
        aria-atomic="false"
      >
        {items.map((it) => (
          <li key={it.key} className="flex items-center gap-2 font-mono text-[11px]">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot[it.status]}`} aria-hidden="true" />
            <span className="text-foreground">{it.from}→{it.to}</span>
            <span className="text-muted-foreground">{it.depart}{it.return ? ` / ${it.return}` : ""}</span>
            <span className="ml-auto text-muted-foreground">{it.status}</span>
            {it.updatedAt != null && (
              <span
                className="text-muted-foreground/70 tabular-nums"
                title={`zaktualizowano: ${timeFmt.format(new Date(it.updatedAt))}`}
              >
                · {timeFmt.format(new Date(it.updatedAt))}
              </span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function PartialResultsBanner({
  message,
  onRetry,
  isRetrying,
}: {
  message: string;
  onRetry: () => void;
  isRetrying: boolean;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-3 flex flex-wrap items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-foreground"
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onRetry}
        disabled={isRetrying}
        className="ml-auto inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-background/60 px-2 py-0.5 text-xs font-medium text-foreground transition hover:bg-background disabled:opacity-50"
      >
        {isRetrying && <Loader2 className="h-3 w-3 animate-spin" />}
        Spróbuj ponownie
      </button>
    </div>
  );
}

const REFETCH_HISTORY_KEY = "refetch-durations-v1";

function loadRefetchHistory(): number[] {
  try {
    const raw = localStorage.getItem(REFETCH_HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "number" && x > 0.3 && x < 120) : [];
  } catch {
    return [];
  }
}

function computeEstimate(arr: number[]): { lo: number; hi: number } | null {
  if (arr.length < 2) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const pick = (p: number) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
  const lo = Math.max(1, Math.round(pick(0.25)));
  const hi = Math.max(lo + 1, Math.round(pick(0.85)));
  return { lo, hi };
}

function RefetchingBanner({ isFetching, completed = 0, total = 0, failed = 0, pairs = 0, activePairs, breakdown }: { isFetching: boolean; completed?: number; total?: number; failed?: number; pairs?: number; activePairs?: number; breakdown?: ProgressBreakdown }) {
  const [elapsed, setElapsed] = useState(0);
  const [estimate, setEstimate] = useState<{ lo: number; hi: number } | null>(null);
  const startRef = useRef<number | null>(null);
  // Klucz „sesji" refetcha — rośnie z każdym startem isFetching. Pozwala
  // schować baner per-refetch i automatycznie pokazać go znów przy kolejnym
  // odświeżeniu (zamknięcie NIE anuluje zapytań, tylko ukrywa UI).
  const [refetchSession, setRefetchSession] = useState(0);
  const [dismissedSession, setDismissedSession] = useState<number | null>(null);

  // Załaduj historię raz przy mountcie.
  useEffect(() => {
    setEstimate(computeEstimate(loadRefetchHistory()));
  }, []);

  // Historia rozmiaru kolejki HTTP/2 w czasie refetcha — resetowana
  // przy starcie nowej sesji (isFetching flip), próbka przy każdej zmianie.
  const queueNow = activePairs != null ? Math.max(0, pairs - activePairs) : 0;
  const [queueHistory, setQueueHistory] = useState<number[]>([]);
  useEffect(() => {
    if (isFetching) setQueueHistory([]);
  }, [isFetching]);
  useEffect(() => {
    if (!isFetching || activePairs == null) return;
    setQueueHistory((prev) => {
      if (prev.length > 0 && prev[prev.length - 1] === queueNow) return prev;
      const next = [...prev, queueNow];
      return next.length > 60 ? next.slice(next.length - 60) : next;
    });
  }, [queueNow, activePairs, isFetching]);
  const showSparkline = queueHistory.length >= 2 && Math.max(...queueHistory) > 0;

  // Mierz aktualny czas trwania refetcha; po zakończeniu zapisz do historii.
  useEffect(() => {
    if (isFetching) {
      startRef.current = Date.now();
      setElapsed(0);
      setRefetchSession((n) => n + 1);
      const id = window.setInterval(() => {
        if (startRef.current) setElapsed((Date.now() - startRef.current) / 1000);
      }, 200);
      return () => window.clearInterval(id);
    }
    if (startRef.current) {
      const dur = (Date.now() - startRef.current) / 1000;
      startRef.current = null;
      if (dur >= 0.3 && dur <= 120) {
        const next = [...loadRefetchHistory(), dur].slice(-25);
        try {
          localStorage.setItem(REFETCH_HISTORY_KEY, JSON.stringify(next));
        } catch {
          // ignore quota errors
        }
        setEstimate(computeEstimate(next));
      }
    }
  }, [isFetching]);

  if (!isFetching) return null;
  if (dismissedSession === refetchSession) return null;

  const elapsedRounded = Math.max(0, Math.round(elapsed));
  const remaining = Math.max(0, total - completed);

  // ETA na bazie tempa BIEŻĄCEGO refetcha: ile zapytań na sekundę faktycznie
  // właśnie idzie. Jeśli jeszcze nic nie wróciło — fallback do historii
  // pełnych refetchów (estimate). Zakres ±30 % żeby nie obiecywać sekund
  // „co do jednej".
  let etaText: string | null = null;
  let etaSec: number | null = null;
  if (total > 1 && remaining > 0 && completed > 0 && elapsed > 0.5) {
    const perQuery = elapsed / completed;
    const mid = perQuery * remaining;
    const lo = Math.max(1, Math.round(mid * 0.7));
    const hi = Math.max(lo + 1, Math.round(mid * 1.3));
    etaText = `pozostało ~${lo}–${hi} s`;
    etaSec = Math.max(1, Math.round(mid));
  } else if (total > 1 && remaining > 0 && estimate) {
    // Mamy historyczny zakres pełnego refetcha — zrzutuj na pozostałą frakcję.
    const frac = remaining / total;
    const lo = Math.max(1, Math.round(estimate.lo * frac));
    const hi = Math.max(lo + 1, Math.round(estimate.hi * frac));
    etaText = `pozostało ~${lo}–${hi} s`;
    etaSec = Math.max(1, Math.round(((estimate.lo + estimate.hi) / 2) * frac));
  }

  // ETA inline obok licznika "trwa X s" — pojedyncza liczba sekund do końca,
  // łatwiejsza do śledzenia kątem oka niż przedział `etaText`.
  const etaInline = etaSec != null ? ` · ETA ~${etaSec} s` : "";

  let hint: string;
  if (estimate) {
    if (elapsed > estimate.hi + 2) {
      hint = `trwa dłużej niż zwykle (${elapsedRounded} s)${etaInline}`;
    } else {
      hint = `~${estimate.lo}–${estimate.hi} s · trwa ${elapsedRounded} s${etaInline}`;
    }
  } else {
    // Brak historii — pierwszy raz na tym urządzeniu.
    hint = elapsed > 1 ? `trwa ${elapsedRounded} s${etaInline}` : "szacowanie czasu…";
  }
  // Ta sama fraza co w LoadingState — patrz formatPairsStatus().
  const pairsHint = ` · ${formatPairsStatus(pairs, activePairs)}`;
  hint = `${hint}${pairsHint}`;

  const showProgress = total > 1;
  const pct = showProgress ? Math.round((completed / total) * 100) : 0;
  const progressLabel = showProgress
    ? failed > 0
      ? `${completed} z ${total} zapytań zakończonych (${failed} z błędem)`
      : `${completed} z ${total} zapytań zakończonych`
    : "";

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      aria-label="Aktualizuję wyniki w tle"
      title="Pobieramy najnowsze ceny od przewoźników. Czas zależy od liczby tras i obciążenia API."
      className="animate-refetch-pulse relative mb-3 flex flex-col items-stretch gap-1.5 rounded-md border border-pink-500/40 bg-pink-500/10 px-3 py-2 pr-8 text-xs font-semibold text-pink-300 motion-reduce:animate-none"
    >
      <button
        type="button"
        onClick={() => setDismissedSession(refetchSession)}
        aria-label="Zamknij baner odświeżania (nie przerywa wyszukiwania w tle)"
        title="Zamknij — wyszukiwanie leci dalej w tle"
        className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-pink-300/70 transition hover:bg-pink-500/20 hover:text-pink-100"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5">
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-pink-400" aria-hidden="true" />
          Aktualizuję wyniki…
        </span>
        {showProgress && (
          // Ten licznik jest jedynym dynamicznym fragmentem, więc trzymamy go
          // w osobnym aria-live + aria-atomic, żeby SR ogłaszał wyłącznie
          // zmianę „X/Y", a nie cały baner co tick.
          <span
            className={failed > 0 ? "font-normal text-amber-300" : "font-normal text-pink-300/90"}
            aria-live="polite"
            aria-atomic="true"
          >
            {progressLabel}
          </span>
        )}
        {etaText && (
          <span className="font-normal text-pink-200" aria-live="off">
            {etaText}
          </span>
        )}
        {/* Wizualnie zostaje hint + tooltip + sparkline; SR dostaje
            czyste zdanie w sr-only regionie poniżej, identyczne brzmieniem
            jak w LoadingState (formatPairsStatusAria). */}
        <span
          className="inline-flex items-center gap-1 font-normal text-pink-300/70"
          aria-hidden="true"
        >
          <span>{hint}</span>
          {pairs > 0 && activePairs != null && activePairs < pairs && <QueueHintTooltip />}
          {showSparkline && <QueueSparkline samples={queueHistory} />}
        </span>
        <span
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {formatPairsStatusAria(pairs, activePairs)}
        </span>
      </div>
      {showProgress && (
        <div
          className="h-1 w-full overflow-hidden rounded-full bg-pink-500/15"
          role="progressbar"
          aria-label="Postęp odświeżania wyników"
          aria-valuenow={completed}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuetext={progressLabel}
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-pink-500 to-pink-400 transition-[width] duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {breakdown && (breakdown.airports.total > 1 || breakdown.dates.total > 1) && (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 text-[11px] font-normal text-pink-200/90">
          {breakdown.airports.total > 1 && (
            <span>
              Pary lotnisk: <span className="font-semibold">{breakdown.airports.checked}/{breakdown.airports.total}</span>
              {breakdown.airports.inFlight > 0 && <> · {breakdown.airports.inFlight} w trakcie</>}
            </span>
          )}
          {breakdown.dates.total > 1 && (
            <span>
              Daty: <span className="font-semibold">{breakdown.dates.checked}/{breakdown.dates.total}</span>
              {breakdown.dates.inFlight > 0 && <> · {breakdown.dates.inFlight} w trakcie</>}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

type AggMetrics = {
  responsesWithMetrics: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  attempts: number;
  retries: number;
  callCount: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
  avgMs: number;
  serverTotalMs: number;
  statusHistogram: Record<string, number>;
  searchIds: string[];
};

// Rozwijane podsumowanie par tras (lotnisko–lotnisko + daty), które nie
// odpowiedziały w bieżącym wyszukiwaniu. Pokazujemy konkretną listę zamiast
// ogólnego komunikatu "część zapytań padła", żeby użytkownik mógł zobaczyć,
// czy problem dotyczy konkretnej trasy/daty czy całej puli.
function FailedPairsDetails({
  failed,
  totalPairs,
}: {
  failed: Array<{ from: string; to: string; depart: string; return?: string; reason: string }>;
  totalPairs: number;
}) {
  const [open, setOpen] = useState(false);
  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" });
  };
  return (
    <details
      className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm">
        <span className="flex items-center gap-2 font-medium text-amber-200">
          <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
          {failed.length} z {totalPairs} {totalPairs === 1 ? "trasy nie odpowiedziało" : "tras nie odpowiedziało"}
        </span>
        <span className="text-xs text-muted-foreground">{open ? "Ukryj" : "Pokaż szczegóły"}</span>
      </summary>
      <ul className="mt-3 space-y-2 text-sm">
        {failed.map((f, i) => (
          <li
            key={`${f.from}-${f.to}-${f.depart}-${f.return ?? ""}-${i}`}
            className="flex flex-col gap-1 rounded-lg bg-background/40 px-3 py-2 sm:flex-row sm:items-baseline sm:justify-between"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs font-semibold text-foreground">
                {f.from} → {f.to}
              </span>
              <span className="text-xs text-muted-foreground">
                {fmtDate(f.depart)}
                {f.return ? ` → ${fmtDate(f.return)}` : ""}
              </span>
            </div>
            <span className="truncate text-xs text-amber-200/80 sm:max-w-[55%] sm:text-right" title={f.reason}>
              {f.reason}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

// Składana sekcja "Szczegóły techniczne" — surface'uje metryki serverFn
// (cache hit-rate, retry, percentyle latency) dla aktualnego wyszukiwania.
// Nie pokazujemy nic przed pierwszą odpowiedzią z serwera; gdy część zapytań
// jeszcze leci, sekcja aktualizuje się w miarę napływania kolejnych metryk.
function TechnicalDetails({
  metrics,
  queriesTotal,
  isFetching,
  failureBuckets,
}: {
  metrics: AggMetrics;
  queriesTotal: number;
  isFetching: boolean;
  failureBuckets?: { "408": number; "429": number; "5xx": number; "inne": number; total: number };
}) {
  const [open, setOpen] = useState(false);
  const totalCalls = metrics.cacheHits + metrics.cacheMisses;
  const hitPct = Math.round(metrics.hitRate * 100);
  const statusEntries = Object.entries(metrics.statusHistogram).sort((a, b) => b[1] - a[1]);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className="mt-8 rounded-lg border border-border/60 bg-card/40 text-xs"
    >
      <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground">
        Szczegóły techniczne
        <span className="text-xs font-normal text-muted-foreground/80">
          ({metrics.responsesWithMetrics}/{queriesTotal} odpowiedzi · cache hit {hitPct}% · {metrics.retries} retry)
        </span>
        {isFetching && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-pink-300">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> aktualizuje się…
          </span>
        )}
      </summary>
      <div className="grid gap-4 border-t border-border/60 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Cache hit-rate">
          <span className="text-base font-semibold text-foreground">{hitPct}%</span>
          <span className="text-muted-foreground"> · {metrics.cacheHits}/{totalCalls} wywołań</span>
        </Stat>
        <Stat label="Retry (cache miss)">
          <span className="text-base font-semibold text-foreground">{metrics.retries}</span>
          <span className="text-muted-foreground"> · {metrics.attempts} prób eSky</span>
        </Stat>
        <Stat label="Latency p50 / p95">
          <span className="text-base font-semibold text-foreground">
            {metrics.p50Ms} / {metrics.p95Ms} ms
          </span>
          <span className="text-muted-foreground"> · max {metrics.maxMs} ms</span>
        </Stat>
        <Stat label="Czas serwera / call avg">
          <span className="text-base font-semibold text-foreground">
            {metrics.serverTotalMs} ms
          </span>
          <span className="text-muted-foreground"> · avg {metrics.avgMs} ms ({metrics.callCount} call)</span>
        </Stat>
        <div className="sm:col-span-2 lg:col-span-4">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Histogram statusów eSky
          </div>
          {statusEntries.length === 0 ? (
            <div className="text-muted-foreground">brak danych</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {statusEntries.map(([k, v]) => (
                <span
                  key={k}
                  className={`rounded-full border px-2 py-0.5 font-mono text-[11px] ${
                    k === "200" || k === "cache"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                      : k === "429" || k === "408" || k === "timeout" || k === "network"
                        ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                        : "border-red-500/30 bg-red-500/10 text-red-300"
                  }`}
                  title={`${v} wywołań`}
                >
                  {k}: {v}
                </span>
              ))}
            </div>
          )}
        </div>
        {failureBuckets && (
          <div className="sm:col-span-2 lg:col-span-4">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Brak wyników wg kategorii (par tras)
            </div>
            {failureBuckets.total === 0 ? (
              <div className="text-muted-foreground">brak — wszystkie pary odpowiedziały</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(["408", "429", "5xx", "inne"] as const).map((k) => {
                  const v = failureBuckets[k];
                  const tone =
                    v === 0
                      ? "border-border/40 bg-muted/20 text-muted-foreground"
                      : k === "5xx"
                        ? "border-red-500/30 bg-red-500/10 text-red-300"
                        : k === "inne"
                          ? "border-slate-500/30 bg-slate-500/10 text-slate-300"
                          : "border-amber-500/30 bg-amber-500/10 text-amber-300";
                  const label =
                    k === "408"
                      ? "408 / timeout"
                      : k === "429"
                        ? "429 / rate-limit"
                        : k === "5xx"
                          ? "5xx / serwer"
                          : "inne / network";
                  return (
                    <span
                      key={k}
                      className={`rounded-full border px-2 py-0.5 font-mono text-[11px] ${tone}`}
                      title={`${v} par tras`}
                    >
                      {label}: {v}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {metrics.searchIds.length > 0 && (
          <div className="sm:col-span-2 lg:col-span-4">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Search IDs (do korelacji z logami)
            </div>
            <div className="flex flex-wrap gap-1.5 font-mono text-[11px] text-muted-foreground">
              {metrics.searchIds.map((id) => (
                <span key={id} className="rounded bg-muted/40 px-1.5 py-0.5">{id}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </details>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-xs">{children}</div>
    </div>
  );
}

function ErrorState({
  apiMessage,
  errorMessage,
  onRetry,
  isRetrying = false,
  attempts = 0,
}: {
  apiMessage?: string | null;
  errorMessage?: string | null;
  onRetry: () => void;
  isRetrying?: boolean;
  attempts?: number;
}) {
  // Status sieci — pozwala odróżnić „brak internetu" od błędu serwera i
  // automatycznie ponowić zapytanie, gdy połączenie wróci.
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      // Auto-retry po powrocie sieci — tylko jeśli aktualnie nie trwa już próba.
      if (!isRetrying) onRetry();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [isRetrying, onRetry]);

  const classified = classifySearchError({ apiMessage, errorMessage, online });
  const Icon = classified.kind === "offline" ? WifiOff : AlertTriangle;
  const retryDisabled = isRetrying || classified.kind === "offline";

  return (
    <div
      className="glass mx-auto max-w-xl rounded-lg p-8 text-center"
      role="alert"
      aria-live="assertive"
      data-error-kind={classified.kind}
    >
      <Icon className="mx-auto mb-3 h-10 w-10 text-destructive" />
      <h2 className="font-display text-xl font-bold">{classified.title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{classified.hint}</p>
      {attempts > 0 && classified.kind !== "offline" && (
        <p className="mt-1 text-xs text-muted-foreground">
          Próby automatycznego ponowienia: {attempts}.
        </p>
      )}
      <Button
        onClick={onRetry}
        variant="hero"
        className="mt-5"
        disabled={retryDisabled}
        aria-label="Spróbuj ponownie pobrać wyniki"
      >
        {isRetrying ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Ponawiam…
          </>
        ) : classified.kind === "offline" ? (
          "Czekam na połączenie…"
        ) : (
          "Spróbuj ponownie"
        )}
      </Button>
    </div>
  );
}

function CanceledState({ completed, total, onRetry }: { completed: number; total: number; onRetry: () => void }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border/60 bg-card/40 py-10 text-center"
    >
      <X className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
      <div className="text-base font-medium text-foreground">Wyszukiwanie anulowane</div>
      <div className="text-sm text-muted-foreground">
        Zatrzymano po {completed} z {total} zapytań. Pozostałe trasy nie zostały sprawdzone.
      </div>
      <Button variant="hero" onClick={onRetry} className="mt-2">
        Wznów wyszukiwanie
      </Button>
    </div>
  );
}

function EmptyState({
  hasActiveFilters: _hasActiveFilters,
  onResetFilters: _onResetFilters,
  onRetry,
  isRetrying,
  directOnly,
  onDisableDirect,
}: {
  hasActiveFilters: boolean;
  onResetFilters: () => void;
  onRetry: () => void;
  isRetrying: boolean;
  directOnly: boolean;
  onDisableDirect: () => void;
}) {
  return (
    <div className="glass mx-auto max-w-xl rounded-lg p-8 text-center">
      <h2 className="font-display text-xl font-bold">Brak ofert dla wybranych kryteriów</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Wyszukiwarka eSky chwilowo mogła nie zwrócić wszystkich ofert dla tej trasy.
        Spróbuj ponownie albo zmień kryteria.
      </p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <Button
          onClick={onRetry}
          variant="hero"
          disabled={isRetrying}
          aria-label="Ponów wyszukiwanie z pominięciem cache"
        >
          {isRetrying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Ponawiam…
            </>
          ) : (
            "Spróbuj ponownie"
          )}
        </Button>
        {directOnly && (
          <Button
            onClick={onDisableDirect}
            variant="outline"
            disabled={isRetrying}
            aria-label="Wyłącz filtr tylko-bezpośrednie i przeszukaj ponownie"
          >
            Pokaż też z przesiadkami
          </Button>
        )}
        <Button asChild variant="ghost" aria-label="Rozpocznij nowe wyszukiwanie od zera">
          <Link to="/">Szukaj od nowa</Link>
        </Button>
      </div>
    </div>
  );
}


// Test-only re-eksport komponentów wewnętrznych. Dzięki temu testy unit-owe
// mogą renderować LoadingState/RefetchingBanner w izolacji, bez zaciągania
// całego drzewa routera. NIE używać w runtime'owym kodzie aplikacji.
export { LoadingState as __LoadingState, RefetchingBanner as __RefetchingBanner };
